import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  CircularProgress,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Close as CloseIcon,
  Chat as ChatIcon,
  MoreVert as MoreVertIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notes as NotesIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { ApiEndPoints, IMAGE_URL, MEDIA_URL, getImageDisplayUrl } from "src/network/endpoints";
import { axiosInstance } from "src/network/adapter";
import { useSelector } from "react-redux";
import authConfig from "src/configs/auth";
import { toast } from "react-hot-toast";
import CustomizeIcon from "src/@core/components/icons/CustomizeIcon";
import { useQuery, useQueryClient, useMutation } from "react-query";
import CancellationDialog from "./CancellationDialog";
import currencyData from "src/constants/currencyData.json";

// Styled Components - Following MenuDialog pattern
const CustomOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000,
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw",
  maxHeight: "95vh",
  width: "900px",
  height: "700px",
  position: "fixed",
  overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "80vh",
    borderRadius: 12,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 25,
  height: 25,
  cursor: "se-resize",
  background:
    "linear-gradient(135deg, rgba(224,224,224,0.9) 0%, rgba(189,189,189,0.9) 100%)",
  borderTopLeftRadius: 20,
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 15,
    height: 15,
    background: `repeating-linear-gradient(
      45deg, 
      rgba(158,158,158,0.8), 
      rgba(158,158,158,0.8) 2px, 
      transparent 2px, 
      transparent 4px
    )`,
  },
  [theme.breakpoints.down("sm")]: {
    width: 20,
    height: 20,
    borderTopLeftRadius: 16,
    "&::before": {
      bottom: 1,
      right: 1,
      width: 12,
      height: 12,
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  background: "white",
  position: "relative",
  zIndex: 10,
  cursor: "move",
  userSelect: "none",
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: 8,
  transform: "translateX(-50%)",
  width: 40,
  height: 4,
  backgroundColor: "#E2E8F0",
  borderRadius: 2,
  cursor: "move",
  "&:hover": { backgroundColor: "#CBD5E1" },
  [theme.breakpoints.down("sm")]: { width: 32, height: 3, top: 6 },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: 16,
  height: "calc(100% - 100px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    padding: "0 12px",
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const ScrollableContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9", borderRadius: 10 },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: 10,
    "&:hover": { background: "#94A3B8" },
  },
});

// Business Info Header
const BusinessHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
  padding: "16px 24px",
  backgroundColor: "#f8f9fa",
  borderBottom: "1px solid #e5e7eb",
  [theme.breakpoints.down("sm")]: { padding: "8px" },
}));

const BusinessInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const BusinessLogo = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  backgroundColor: "#FFD700",
  color: "#000000",
  fontWeight: "bold",
  fontSize: "1.2rem",
  [theme.breakpoints.down("sm")]: {
    width: 50,
    height: 50,
    fontSize: "0.9rem",
  },
}));

// User Card with Purple Background
const UserCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f0f4ff",
  border: "1px solid #d1d5db",
  borderRadius: theme.spacing(2),
  margin: theme.spacing(2, 0),
  padding: "8px 24px",
  [theme.breakpoints.down("sm")]: { padding: "8px 16px" },
  [theme.breakpoints.down(400)]: { padding: "4px 12px" },
}));

const UserInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

// Tabs
const CustomTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTab-root": {
    textTransform: "lowercase",
    fontWeight: 600,
    fontSize: "1rem",
    minHeight: 46,
    padding: "8px 16px",
    borderRadius: "12px 12px 0 0",
    marginRight: 4,
    color: "black",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&.Mui-selected": {
      color: "#7C3AED",
      backgroundColor: "#f0f4ff",
      fontWeight: 700,
    },
    "&:hover": { backgroundColor: "#f0f4ff", color: "#7C3AED" },
  },
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-flexContainer": { borderBottom: "2px solid #E2E8F0" },
}));

// Colored Table for Order Items
const ColoredTable = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  border: "1px solid #e5e7eb",
  borderRadius: theme.spacing(1),
  // Removed nth-of-type background colors - now using status-based colors from itemStatus
  "& .MuiTableCell-root": {
    padding: "4px !important",
  },
  "& .MuiTableCell-head": {
    padding: "4px !important",
  },
  "& .MuiTableCell-body": {
    padding: "4px !important",
  },
  "& table .MuiTableCell-root": {
    padding: "4px !important",
  },
  "& .MuiTable-root .MuiTableCell-root": {
    padding: "4px !important",
  },
}));

// Payment Summary
const PaymentSummary = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

// Tip Section
const TipSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const TipButton = styled(Button)(({ theme, selected }) => ({
  textTransform: "lowercase",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(0.5, 2),
  margin: theme.spacing(0.5),
  backgroundColor: selected ? "#7C3AED" : "transparent",
  color: selected ? "#ffffff" : "#6b7280",
  border: "1px solid #d1d5db",
  "&:hover": {
    backgroundColor: selected ? "#6d28d9" : "#f3f4f6",
  },
}));

// Action Buttons
const ActionButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  textTransform: "lowercase",
  fontSize: "0.8rem",
  fontWeight: 700,
  "&.cancel": {
    background: "#f3f4f6",
    color: "#6b7280",
    border: "2px solid #e5e7eb",
  },
  "&.pay": {
    background: "#0085FF",
    color: "white",
  },
}));

/**
 * OrderDetailsDialog Component
 *
 * This component displays detailed order information in a resizable and movable dialog.
 * It follows the MenuDialog pattern for consistent UI/UX.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {string} props.orderId - Order ID for API calls
 * @param {string} props.locationId - Business location ID for API calls (optional)
 */
const OrderDetailsDialog = ({ open, onClose, orderId, locationId, conversationId }) => {
  const theme = useTheme();
  const dialogRef = useRef(null);
  const popupRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const isClosingRef = useRef(false);

  // get current user for authentication
  const currentUser = useSelector((state) => state.auth.user);
  const storedUserId =
    typeof window !== "undefined"
      ? window.localStorage.getItem(authConfig.storageUserIdKeyName)
      : null;
  const currentUserId = currentUser?.userId ?? storedUserId;
  const queryClient = useQueryClient();

  // state management
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTip, setSelectedTip] = useState("10%");
  const [customTip, setCustomTip] = useState("0.00");
  const [expandedSides, setExpandedSides] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState(null);
  const [cancelledItems, setCancelledItems] = useState({}); // Store cancelled items data
  const [pendingCancellations, setPendingCancellations] = useState([]); // Store all pending cancellations before API call
  const [paymentErrorDialog, setPaymentErrorDialog] = useState({ open: false, message: "" });
  const [manualPaymentLoading, setManualPaymentLoading] = useState(false);

  // dialog positioning and resizing
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 100, y: 100 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // React Query for fetching order data (only once when dialog opens)
  const {
    data: orderData,
    isLoading: loading,
    error,
  } = useQuery(
    ["order-details", orderId, locationId],
    async () => {
      const url = locationId 
        ? `${ApiEndPoints.ORDER.getOrderById(orderId)}?locationId=${locationId}`
        : ApiEndPoints.ORDER.getOrderById(orderId);
      const response = await axiosInstance.get(url);

      if (response.data?.status === "success" && response.data?.data?.order) {
        return response.data.data.order;
      } else {
        throw new Error("Invalid response format");
      }
    },
    {
      enabled: open && !!orderId,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      onError: (error) => {
        console.error("Error fetching order details:", error);
        toast.error("failed to load order details");
      },
    }
  );

  // Drag and resize functionality (from MenuDialog)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - 400;
        const maxY = window.innerHeight - 300;
        setDialogPosition({
          x: Math.max(0, Math.min(e.clientX - dragOffset.x, maxX)),
          y: Math.max(0, Math.min(e.clientY - dragOffset.y, maxY)),
        });
      } else if (isResizing && dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;

        if (newWidth > 400 && newWidth < window.innerWidth * 0.95) {
          dialogRef.current.style.width = `${newWidth}px`;
        }
        if (newHeight > 300 && newHeight < window.innerHeight * 0.95) {
          dialogRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Initialize dialog position when opened
  useEffect(() => {
    if (open) {
      const isMobile = window.innerWidth < 960;
      const dialogWidth = isMobile ? window.innerWidth * 0.9 : 900;
      const dialogHeight = isMobile ? window.innerHeight * 0.8 : 700;
      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;

      setDialogPosition({
        x: Math.max(0, centerX),
        y: Math.max(0, centerY),
      });
    }
  }, [open]);

  // Event handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle search input
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Handle delete icon click - toggle selection mode only
  const handleDeleteClick = () => {
    // Toggle selection mode
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      // Clear all selections and pending cancellations when exiting selection mode
      selectedItems.forEach((itemKey) => {
        removeItemFromPendingCancellations(itemKey);
      });
      setSelectedItems(new Set());
    }
  };

  // Helper function to add item to pending cancellations
  const addItemToPendingCancellations = (itemKey) => {
    const orderItems = orderData?.groupOrder?.userOrders || [];
    const parts = itemKey.split("-");
    const userIndex = parseInt(parts[0]);
    const itemIndex = parseInt(parts[1]);
    const variantIndex = parts.length > 2 ? parseInt(parts[2]) : null;

    const userOrder = orderItems[userIndex];
    const item = userOrder?.cart?.[itemIndex];

    if (item) {
      let cancellationObject;

      if (variantIndex !== null && item.variantEnabled && item.variants) {
        // Variant item
        const variant = item.variants[variantIndex];
        if (variant) {
          const allItemIds = variant.items?.map((i) => i._id) || [];
          const totalQuantity =
            variant.items?.reduce((total, i) => total + (i.quantity || 0), 0) ||
            0;

          cancellationObject = {
            cartItemId: item._id,
            cancellationType: "partial",
            quantityToCancel: totalQuantity,
            reason: "bulk cancellation from selection",
            itemIds: allItemIds,
            variantName: variant.variantName,
          };
        }
      } else {
        // Non-variant item
        const allItemIds = item.items?.map((i) => i._id) || [];
        const totalQuantity = item.quantity || 0;

        cancellationObject = {
          cartItemId: item._id,
          cancellationType: "full",
          quantityToCancel: totalQuantity,
          reason: "bulk cancellation from selection",
          itemIds: allItemIds,
        };
      }

      if (cancellationObject) {
        setPendingCancellations((prev) => [...prev, cancellationObject]);

        // Add local pending cancellation for strikethrough/labeling in UI
        const cancellationKey = cancellationObject.variantName
          ? `${cancellationObject.cartItemId}-${cancellationObject.variantName}`
          : cancellationObject.cartItemId;

        setCancelledItems((prev) => ({
          ...prev,
          [cancellationKey]: {
            quantityCancelled: cancellationObject.quantityToCancel,
            itemIds: cancellationObject.itemIds,
            variantName: cancellationObject.variantName || null,
            cancelledAt: new Date().toISOString(),
          },
        }));
      }
    }
  };

  // Helper function to remove item from pending cancellations
  const removeItemFromPendingCancellations = (itemKey) => {
    const orderItems = orderData?.groupOrder?.userOrders || [];
    const parts = itemKey.split("-");
    const userIndex = parseInt(parts[0]);
    const itemIndex = parseInt(parts[1]);
    const variantIndex = parts.length > 2 ? parseInt(parts[2]) : null;

    const userOrder = orderItems[userIndex];
    const item = userOrder?.cart?.[itemIndex];

    if (item) {
      let cancellationKey;

      if (variantIndex !== null && item.variantEnabled && item.variants) {
        // Variant item
        const variant = item.variants[variantIndex];
        cancellationKey = variant ? `${item._id}-${variant.variantName}` : null;
      } else {
        // Non-variant item
        cancellationKey = item._id;
      }

      if (cancellationKey) {
        // Remove from pending cancellations
        setPendingCancellations((prev) =>
          prev.filter((cancel) => {
            const cancelKey = cancel.variantName
              ? `${cancel.cartItemId}-${cancel.variantName}`
              : cancel.cartItemId;
            return cancelKey !== cancellationKey;
          })
        );

        // Remove from cancelled items display
        setCancelledItems((prev) => {
          const updated = { ...prev };
          delete updated[cancellationKey];
          return updated;
        });
      }
    }
  };

  // Handle item selection
  const handleItemSelect = (itemKey) => {
    const newSelectedItems = new Set(selectedItems);
    const isCurrentlySelected = newSelectedItems.has(itemKey);

    if (isCurrentlySelected) {
      newSelectedItems.delete(itemKey);
      // Remove from pending cancellations when deselected
      removeItemFromPendingCancellations(itemKey);
    } else {
      newSelectedItems.add(itemKey);
      // Automatically add to pending cancellations when selected
      addItemToPendingCancellations(itemKey);
    }

    setSelectedItems(newSelectedItems);
  };

  // Handle select all items
  const handleSelectAll = () => {
    if (!orderData) return;

    const orderItems = orderData?.groupOrder?.userOrders || [];
    const allItemKeys = new Set();

    orderItems.forEach((userOrder, userIndex) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item, itemIndex) => {
        if (item.variantEnabled && item.variants) {
          // For variant items, add each variant as separate item
          item.variants.forEach((variant, variantIndex) => {
            allItemKeys.add(`${userIndex}-${itemIndex}-${variantIndex}`);
          });
        } else {
          // For non-variant items
          allItemKeys.add(`${userIndex}-${itemIndex}`);
        }
      });
    });

    // Automatically add all items to pending cancellations
    allItemKeys.forEach((itemKey) => {
      addItemToPendingCancellations(itemKey);
    });

    setSelectedItems(allItemKeys);
  };

  // Handle deselect all items
  const handleDeselectAll = () => {
    // Remove all items from pending cancellations
    selectedItems.forEach((itemKey) => {
      removeItemFromPendingCancellations(itemKey);
    });

    setSelectedItems(new Set());
  };

  // Get total item count for select all functionality (counting variants separately)
  const getTotalItemCount = () => {
    if (!orderData) return 0;

    const orderItems = orderData?.groupOrder?.userOrders || [];
    let totalCount = 0;

    orderItems.forEach((userOrder) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item) => {
        if (item.variantEnabled && item.variants) {
          // Count each variant as a separate item
          totalCount += item.variants.length;
        } else {
          // Count non-variant items
          totalCount += 1;
        }
      });
    });

    return totalCount;
  };

  // Handle opening cancellation dialog from quantity controls
  const handleOpenCancellationDialog = (
    item,
    userIndex,
    itemIndex,
    action,
    variantName = null
  ) => {
    if (!orderData || !item) {
      toast.error("invalid item for cancellation");
      return;
    }

    // For increase action, check if quantity was previously decreased
    if (action === "increase") {
      // Check if this item has been modified (quantity decreased)
      const hasBeenDecreased = item.cancellation?.requested || false;
      if (!hasBeenDecreased) {
        toast.error("you can only increase quantity after decreasing it first");
        return;
      }
    }
    
    // Get pending cancellations for this item (from local state)
    const cancellationKey = variantName ? `${item._id}-${variantName}` : item._id;
    const localPendingCancellation = cancelledItems[cancellationKey];
    
    // Merge API cancelled items with local pending cancellations
    // ONLY include approved or pending_approval items (NOT rejected)
    let apiCancelledItems = [];
    if (item.variantEnabled && item.variants && variantName) {
      // For variant items, get cancelled items from the specific variant
      const selectedVariant = item.variants.find(v => v.variantName === variantName);
      apiCancelledItems = selectedVariant?.items?.filter(i => 
        i.cancellation?.requested && 
        (i.cancellation?.status === "approved" || i.cancellation?.status === "pending_approval")
      ).map(i => i.itemNumber) || [];
    } else {
      // For non-variant items - ONLY include approved or pending_approval
      apiCancelledItems = item.items?.filter(i => 
        i.cancellation?.requested && 
        (i.cancellation?.status === "approved" || i.cancellation?.status === "pending_approval")
      ).map(i => i.itemNumber) || [];
    }
    const localCancelledItems = localPendingCancellation?.itemIds || [];
    const allCancelledItems = [...new Set([...apiCancelledItems, ...localCancelledItems])];
    
    // If this is a variant item and variantName is provided, filter to show only that variant
    let filteredItem = item;
    if (item.variantEnabled && variantName && item.variants) {
      const selectedVariant = item.variants.find(
        (variant) => variant.variantName === variantName
      );
      if (selectedVariant) {
        // Create a filtered item with only the selected variant
        filteredItem = {
          ...item,
          variants: [selectedVariant],
          // Update total quantity to match the selected variant
          quantity:
            selectedVariant.items?.reduce(
              (total, variantItem) => total + (variantItem.quantity || 0),
              0
            ) || 0,
        };
      }
    }
    
    // Merge local pending cancellations into the filtered item so CancellationDialog can see them
    if (localPendingCancellation && localPendingCancellation.itemIds.length > 0) {
      if (filteredItem.variantEnabled && filteredItem.variants) {
        // For variant items, mark items as cancelled with pending_approval status
        filteredItem.variants = filteredItem.variants.map(variant => {
          if (variant.variantName === variantName) {
            return {
              ...variant,
              items: variant.items?.map(i => {
                if (localCancelledItems.includes(i._id)) {
                  // Don't override if item already has approved status
                  if (i.cancellation?.status === "approved") {
                    return i;
                  }
                  return {
                    ...i,
                    cancellation: {
                      requested: true,
                      status: 'local_pending'
                    }
                  };
                }
                return i;
              })
            };
          }
          return variant;
        });
    } else {
        // For non-variant items, mark items as cancelled with pending_approval status
        filteredItem.items = filteredItem.items?.map(i => {
          if (localCancelledItems.includes(i._id)) {
            // Don't override if item already has approved status
            if (i.cancellation?.status === "approved") {
              return i;
            }
            return {
              ...i,
              cancellation: {
                requested: true,
                status: 'local_pending'
              }
            };
          }
          return i;
        });
      }
    }
    
    setSelectedCartItem(filteredItem);
    setCancellationDialogOpen(true);
  };

  // Clear all pending cancellations
  const handleClearAllCancellations = () => {
    setPendingCancellations([]);
    setCancelledItems({});
    toast.success("all pending cancellations cleared");
  };

  // Handle cancellation dialog close
  const handleCancellationDialogClose = () => {
    setCancellationDialogOpen(false);
    setSelectedCartItem(null);
    // Clear selection and exit selection mode
    setSelectedItems(new Set());
    setIsSelectionMode(false);
  };

  // Handle cancellation submission - Add to pending cancellations array
  const handleCancellationSubmit = (cancellationObject) => {
    // Add cancellation object to pending cancellations array, replacing any existing one for this item
    setPendingCancellations((prev) => {
        // Filter out existing cancellation for the same cartItemId and variantName (if applicable)
        const updated = prev.filter(c => {
            if (c.cartItemId !== cancellationObject.cartItemId) return true;
            if (c.variantName !== cancellationObject.variantName) return true;
            return false;
        });
        return [...updated, cancellationObject];
    });

    // Update cancelled items for strikethrough display
    const { cartItemId, quantityToCancel, itemIds, variantName } =
      cancellationObject;

    // Create a unique key for this cancellation (cartItemId + variantName if exists)
    const cancellationKey = variantName
      ? `${cartItemId}-${variantName}`
      : cartItemId;

    setCancelledItems((prev) => ({
      ...prev,
      [cancellationKey]: {
        quantityCancelled: quantityToCancel,
        itemIds: itemIds || [],
        variantName: variantName || null,
        cancelledAt: new Date().toISOString(),
      },
    }));

    // Close dialog
    setCancellationDialogOpen(false);
    setSelectedCartItem(null);

    // Show success message
    toast.success(
      "cancellation added. click 'submit all cancellations' to apply"
    );
  };

  // Handle successful cancellation
  const handleCancellationSuccess = () => {
    // Clear selection and exit selection mode
    setSelectedItems(new Set());
    setIsSelectionMode(false);
  };

  // React Query mutation for cancellation request
  const cancellationMutation = useMutation(
    async (cancellationData) => {
      const response = await axiosInstance.put(
        ApiEndPoints.ORDER.requestCancellation(orderData._id),
        cancellationData
      );

      if (response.data?.status === "success") {
        return response.data;
      } else {
        throw new Error(
          response.data?.message || "failed to submit cancellation request"
        );
      }
    },
    {
      onSuccess: () => {
        toast.success("cancellation request submitted  ");
        queryClient.invalidateQueries(["order-details"]);
        handleCancellationSuccess();
        // Clear pending cancellations after successful submission
        setPendingCancellations([]);
        setCancelledItems({});
      },
      onError: (error) => {
        console.error("error submitting cancellation request:", error);
        toast.error(
          error.response?.data?.message ||
            "failed to submit cancellation request"
        );
      },
    }
  );

  // Handle submit all cancellations
  const handleSubmitAllCancellations = () => {
    if (pendingCancellations.length === 0) {
      toast.error("no cancellations to submit");
      return;
    }

    // Wrap all cancellations in the expected format
    const cancellationData = {
      cancellations: pendingCancellations,
    };

    // Call the mutation
    cancellationMutation.mutate(cancellationData);
  };

  // Filter order items based on search term (client-side filtering)
  const filterOrderItems = (items) => {
    if (!searchTerm.trim()) return items;

    const searchLower = searchTerm.toLowerCase();
    return items.filter((userOrder) => {
      const userCart = userOrder.cart || [];
      return userCart.some((item) => {
        // Search in item name
        if (item.menuItemId?.name?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in item code
        if (item.menuItemId?.code?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in user name
        if (userOrder.userFullName?.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search in notes
        if (item.note?.toLowerCase().includes(searchLower)) {
          return true;
        }
        return false;
      });
    });
  };

  const handleTipSelect = (tip) => {
    setSelectedTip(tip);
    if (tip === "no tip") {
      setCustomTip("0.00");
    } else if (tip !== "custom") {
      // Handle both string percentages and numeric values
      const percentage =
        typeof tip === "string" ? parseInt(tip.replace("%", "")) : tip;

      // Ensure orderData is available before calculating
      if (!orderData) {
        console.warn("Order data not available for tip calculation");
        setCustomTip("0.00");
        return;
      }

      // Get user order to determine payment type
      const userOrder = orderData?.groupOrder?.userOrders?.find(
        (userOrder) =>
          userOrder.userId?._id === currentUserId ||
          userOrder.userId?._id === storedUserId
      );
      
      // Determine if this is a "pay for all" scenario
      // Check userOrder.paymentFlow first, then paymentType
      const userPaymentFlowType = userOrder?.paymentFlow?.type;
      const isIndividual = userPaymentFlowType === "individual" || 
                          (userPaymentFlowType === null && userOrder?.paymentType === "individual");
      const isSplitEvenly = userPaymentFlowType === "split_evenly" ||
                           userOrder?.paymentType === "split_evenly" ||
                           orderData?.paymentType === "split_evenly" ||
                           orderData?.paymentFlow?.type === "split_evenly";
      const isPayForAll = !isIndividual && !isSplitEvenly && (
        userPaymentFlowType === "pay_for_all" ||
        userOrder?.paymentType === "pay_for_all" ||
        orderData?.paymentType === "pay_for_all" ||
        orderData?.paymentFlow?.type === "pay_for_all"
      );
      
      // Calculate base amount for tip calculation
      // For "pay for all", use group subtotal; for individual/split_evenly, use user's amountToPay
      const baseAmount = isPayForAll
        ? (orderData.groupSubtotal || orderData.totalAmount || 0)
        : (userOrder?.amountToPay > 0 ? userOrder.amountToPay : userOrder?.subtotal || orderData.groupSubtotal || 0);

      // Validate inputs to prevent NaN
      if (isNaN(percentage) || isNaN(baseAmount) || baseAmount <= 0) {
        console.warn("Invalid tip calculation inputs:", {
          percentage,
          baseAmount,
          tip,
          orderData,
        });
        setCustomTip("0.00");
        return;
      }

      const calculatedTip = ((baseAmount * percentage) / 100).toFixed(2);
      setCustomTip(calculatedTip);
    }
  };

  const handleCustomTipChange = (event) => {
    const value = event.target.value;
    // Validate that the input is a valid number
    if (value === "" || (!isNaN(parseFloat(value)) && isFinite(value))) {
      setCustomTip(value);
      setSelectedTip("custom");
    }
  };

  // Toggle sides expansion
  const toggleSidesExpansion = (itemKey) => {
    setExpandedSides((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // Close popup function
  const closePopup = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    
    isClosingRef.current = false;
    setPaymentLoading(false);
  };

  // PostMessage listener for payment success
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "order-payment-success") {
        closePopup();
        queryClient.invalidateQueries(["order-details"]);
        toast.success("Payment processed  ");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closePopup();
    };
  }, []);

  // Payment processing function
  const handlePayment = async (paymentAmount) => {
    if (!orderId || !orderData) {
      toast.error("order data not available");
      return;
    }

    if (popupRef.current && !popupRef.current.closed) {
      toast.error("Payment popup is already open");
      return;
    }

    try {
      setPaymentLoading(true);

      // Get currency from order
      const currencyCode = getOrderCurrency();
      
      // Check minimum amount requirement
      if (currencyCode) {
        try {
          const currencyResponse = await axiosInstance.get(
            ApiEndPoints.COMMON.currency,
            {
              params: {
                search: currencyCode.toUpperCase(),
              },
            }
          );

          if (
            currencyResponse.data?.status === "success" &&
            currencyResponse.data?.data?.currencies?.length > 0
          ) {
            const currencyData = currencyResponse.data.data.currencies[0];
            const minimumAmount = currencyData.minimumAmount || 0;

            if (Number(paymentAmount) < minimumAmount) {
              setPaymentLoading(false);
              toast.error(
                `the minimum in app payment amount is ${formatPrice(minimumAmount, currencyCode)}, increase or wait for a team member to process your manual payment via card or cash`
              );
              return;
            }
          }
        } catch (currencyError) {
          console.error("Error fetching currency data:", currencyError);
          // Continue with payment even if currency API fails
        }
      }

      let paymentType;
      if (upfrontPayment > 0) {
        paymentType = "upfront";
      } else if (remainingPayment > 0) {
        paymentType = "remaining";
      } else {
        paymentType = "full";
      }

      // Generate success and cancel URLs
      const successUrl = `${window.location.origin}/order/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/order/cancel`;

      // Calculate base amount (without tip) - paymentAmount already includes tip
      const baseAmount = Number(paymentAmount) - (Number(tipAmount) || 0);
      const tipAmountValue = Number(tipAmount) || 0;

      const paymentData = {
        type: "order",
        orderId: orderId,
        amount: baseAmount,
        tipAmount: tipAmountValue,
        successUrl,
        cancelUrl,
      };

      const response = await axiosInstance.post(
        ApiEndPoints.USER.createCheckoutSession,
        paymentData
      );

      if (response.data?.status === "success" && response.data?.data?.checkoutUrl) {
        const checkoutUrl = response.data.data.checkoutUrl;
        
        // Calculate center position for popup
        const width = 600;
        const height = 800;
        const windowLeft = window.screenLeft || window.screenX || 0;
        const windowTop = window.screenTop || window.screenY || 0;
        const windowWidth = window.outerWidth || window.innerWidth;
        const windowHeight = window.outerHeight || window.innerHeight;
        const left = windowLeft + (windowWidth - width) / 2;
        const top = windowTop + (windowHeight - height) / 2;

        // Open popup
        popupRef.current = window.open(
          checkoutUrl,
          "popup",
          `width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)},scrollbars=yes,resizable=yes`
        );

        // Poll to check popup status
        checkIntervalRef.current = setInterval(() => {
          if (isClosingRef.current) return;

          if (!popupRef.current) {
            if (checkIntervalRef.current) {
              clearInterval(checkIntervalRef.current);
              checkIntervalRef.current = null;
            }
            return;
          }

          if (popupRef.current.closed) {
            closePopup();
            toast.error("Payment was cancelled. Please try again.");
            return;
          }

          try {
            const popupLocation = popupRef.current.location;
            const pathname = popupLocation.pathname;
            
            if (pathname === "/order/success" || pathname === "/order/cancel" || 
                popupLocation.href.includes("/order/success") || 
                popupLocation.href.includes("/order/cancel")) {
              
              if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
              }

              if (pathname === "/order/success" || popupLocation.href.includes("/order/success")) {
                queryClient.invalidateQueries(["order-details"]);
                toast.success("Payment processed  ");
              } else {
                toast.error("Payment was cancelled. Please try again.");
              }

              closePopup();
            }
          } catch (e) {
            // Cross-origin error expected
          }
        }, 500);
      } else {
        toast.error(response.data?.message || "Failed to get checkout URL");
        setPaymentLoading(false);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      const errMsg = error.response?.data?.message || "payment processing failed";
      setPaymentErrorDialog({ open: true, message: errMsg });
      setPaymentLoading(false);
    }
  };

  const handleManualPaymentRequest = async () => {
    if (!conversationId) return;
    setManualPaymentLoading(true);
    try {
      await axiosInstance.post(ApiEndPoints.CHAT.sendManualPaymentMessage, { conversationId });
      toast.success("manual payment request sent successfully");
      setPaymentErrorDialog({ open: false, message: "" });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send manual payment request");
    } finally {
      setManualPaymentLoading(false);
    }
  };

  // Calculate totals with proper tax breakdown for group orders
  const subtotal = orderData?.groupSubtotal || orderData?.subtotal || 0;
  const tax = orderData?.tax || 0;
  const totalAmount = orderData?.groupSubtotal || orderData?.totalAmount || 0;
  const tipAmount = parseFloat(customTip) || 0;

  // Calculate upfront payment
  const calculateUpfrontPayment = () => {
    if (
      !orderData?.paymentSettings?.upfrontPercentage ||
      orderData.paymentSettings.upfrontPercentage <= 0
    ) {
      return 0;
    }

    // For closed orders, upfront payment is already complete - only final payment remains
    const isClosedOrder = orderData?.orderStatus === "closed order";
    if (isClosedOrder) {
      return 0;
    }

    const upfrontPercentage = orderData.paymentSettings.upfrontPercentage;

    // Get the current user's order first
    const userOrder = orderData?.groupOrder?.userOrders?.find(
      (userOrder) =>
        userOrder.userId?._id === currentUserId ||
        userOrder.userId?._id === storedUserId
    );

    if (!userOrder) {
      return 0;
    }

    // Check userOrder.paymentFlow first (per user payment flow) - this is the most accurate
    // Then check userOrder.paymentType, then order-level paymentType
    const userPaymentFlowType = userOrder.paymentFlow?.type;
    const userPaymentType = userOrder.paymentType;
    const orderPaymentType = orderData?.paymentType;
    const orderPaymentFlowType = orderData?.paymentFlow?.type;
    
    // Check if ANY user has pay_for_all payment flow (for group upfront calculation)
    const allUserOrders = orderData?.groupOrder?.userOrders || [];
    const anyUserHasPayForAll = allUserOrders.some((uo) => {
      return uo.paymentFlow?.type === "pay_for_all" || uo.paymentType === "pay_for_all";
    });
    
    // If user has explicitly initiated individual payment, use individual calculation
    const isIndividual = userPaymentFlowType === "individual" || 
                        (userPaymentFlowType === null && userPaymentType === "individual");
    
    // Check for split_evenly - each user pays equal share
    const isSplitEvenly = userPaymentFlowType === "split_evenly" ||
                         userPaymentType === "split_evenly" ||
                         orderPaymentType === "split_evenly" ||
                         orderPaymentFlowType === "split_evenly";
    
    // If user has pay_for_all, order-level is pay_for_all, OR any user has pay_for_all
    // (and user hasn't explicitly chosen individual), use group totals
    const isPayForAll = !isIndividual && !isSplitEvenly && (
      userPaymentFlowType === "pay_for_all" ||
      userPaymentType === "pay_for_all" ||
      orderPaymentType === "pay_for_all" ||
      orderPaymentFlowType === "pay_for_all" ||
      anyUserHasPayForAll
    );

    // split_evenly is always a final-payment flow — upfront is collected before split
    // requests are sent. By the time paymentType === "split_evenly", the upfront phase
    // is already complete. Applying upfront% to amountToPay (the split share) would
    // double-count because the split share itself was derived from the post-upfront balance.
    if (isSplitEvenly) {
      return 0;
    }

    // If payment is pay_for_all, upfront is a group responsibility
    if (isPayForAll) {
      const groupSubtotal = orderData?.groupSubtotal || 0;
      const totalRequiredUpfront = (groupSubtotal * upfrontPercentage) / 100;
      // Use authoritative backend field; fall back to summing userOrders only if missing
      const totalPaid = orderData?.totalPaidAmount
        ?? (orderData?.groupOrder?.userOrders || []).reduce((sum, uo) => sum + (uo.amountPaid || 0), 0);
      const remainingUpfront = Math.max(0, totalRequiredUpfront - totalPaid);
      return Math.round(remainingUpfront * 100) / 100;
    }

    // For individual payments, check if any user has pay_for_all
    // If yes, use remaining group upfront instead of individual upfront
    if (anyUserHasPayForAll) {
      const groupSubtotal = orderData?.groupSubtotal || 0;
      const totalRequiredUpfront = (groupSubtotal * upfrontPercentage) / 100;
      const totalPaid = orderData?.totalPaidAmount
        ?? (orderData?.groupOrder?.userOrders || []).reduce((sum, uo) => sum + (uo.amountPaid || 0), 0);
      const remainingUpfront = Math.max(0, totalRequiredUpfront - totalPaid);
      return Math.round(remainingUpfront * 100) / 100;
    }
    
    // For individual payments without pay_for_all, calculate upfront on user's current subtotal
    
    // Check if there are any items that have NOT completed upfront payment
    // We trust the backend's isUpfrontCompleted flag
    const hasPendingUpfrontItems = userOrder.cart && userOrder.cart.some(item => item.isUpfrontCompleted === false);

    if (hasPendingUpfrontItems) {
      // If there are items needing upfront payment, use the backend's calculated amountToPay
      // This correctly handles cases where some items are already paid and new items are added
      return userOrder.amountToPay || 0;
    } else {
      // If all items have completed upfront payment, no upfront is required
      return 0;
    }
  };

  const upfrontPayment = calculateUpfrontPayment();
  
  // Calculate remaining payment
  const calculateRemainingPayment = () => {
    const userOrder = orderData?.groupOrder?.userOrders?.find(
      (userOrder) =>
        userOrder.userId?._id === currentUserId ||
        userOrder.userId?._id === storedUserId
    );

    if (!userOrder) {
      return 0;
    }

    // If there's an upfront payment to be made, that's the remaining payment
    if (upfrontPayment > 0) {
      return upfrontPayment;
    }

    // Check order-level payment type and status
    const orderPaymentType = orderData?.paymentType;
    const isClosedOrder = orderData?.orderStatus === "closed order";
    
    // Check userOrder.paymentFlow first (per user payment flow) - this is the most accurate
    const userPaymentFlowType = userOrder.paymentFlow?.type;
    const userPaymentType = userOrder.paymentType;
    const orderPaymentFlowType = orderData?.paymentFlow?.type;
    
    // If user has explicitly initiated individual payment, use individual calculation
    const isIndividual = userPaymentFlowType === "individual" || 
                        (userPaymentFlowType === null && userPaymentType === "individual");
    
    // Check for split_evenly - each user pays equal share
    const isSplitEvenly = userPaymentFlowType === "split_evenly" ||
                         userPaymentType === "split_evenly" ||
                         orderPaymentType === "split_evenly" ||
                         orderPaymentFlowType === "split_evenly";
    
    // If user has pay_for_all or order-level is pay_for_all (and user hasn't explicitly chosen individual), use group totals
    const isPayForAll = !isIndividual && !isSplitEvenly && (
      userPaymentFlowType === "pay_for_all" ||
      userPaymentType === "pay_for_all" ||
      orderPaymentType === "pay_for_all" ||
      orderPaymentFlowType === "pay_for_all"
    );

    // For split_evenly: each user's obligation is exactly their amountToPay.
    // DO NOT use userOrder.remainingAmount here — that field is updated by the proportional
    // group-payment distribution and reflects the user's outstanding share of the *total*
    // bill, not their split-request obligation.  A user with amountToPay=0 (e.g. an
    // owner/team-member not included in the split) must see 0 remaining.
    if (isSplitEvenly) {
      return Math.max(0, userOrder.amountToPay || 0);
    }

    // For pay_for_all (either at order level or user level), calculate remaining based on group total
    if (isPayForAll) {
      // Use authoritative backend field; fall back to computing from groupSubtotal only if missing
      const remaining = orderData?.remainingAmount
        ?? Math.max(0, (orderData?.groupSubtotal || 0) - (orderData?.totalPaidAmount || 0));
      return Math.max(0, remaining);
    }

    // For individual payments, calculate remaining amount
    // When upfront is done and order is closed, use API's remainingAmount if available (more accurate)
    if (isClosedOrder && upfrontPayment === 0) {
      // For closed orders with upfront completed, prioritize API's remainingAmount
      if (userOrder.remainingAmount !== undefined && userOrder.remainingAmount !== null && userOrder.remainingAmount > 0) {
        return Math.max(0, userOrder.remainingAmount);
      }
      
      // Fallback: calculate remaining as full subtotal minus amount paid
      const userSubtotal = userOrder.subtotal || 0;
      const userPaid = userOrder.amountPaid || 0;
      const userRemaining = Math.max(0, userSubtotal - userPaid);
      
      // Only return remaining if there's actually something to pay
      if (userRemaining > 0) {
        return userRemaining;
      }
    }
    
    // For regular individual payments (not closed or upfront not done), use backend's remainingAmount.
    // amountToPay − amountPaid can diverge when items are added after upfront is collected;
    // remainingAmount is the authoritative server-side field that reflects actual outstanding balance.
    const userRemaining = userOrder.remainingAmount ?? Math.max(0, (userOrder.amountToPay || 0) - (userOrder.amountPaid || 0));
    return Math.max(0, userRemaining);
  };
  
  const remainingPayment = calculateRemainingPayment();

  // For split_per_person, calculate grand total based on user's individual amount
  const getUserOrder = () => {
    return orderData?.groupOrder?.userOrders?.find(
      (userOrder) =>
        userOrder.userId?._id === currentUserId ||
        userOrder.userId?._id === storedUserId
    );
  };

  const userOrder = getUserOrder();
  
  // Determine if this is a "pay for all" scenario
  // Check userOrder.paymentFlow first, then paymentType
  const userPaymentFlowType = userOrder?.paymentFlow?.type;
  const isIndividual = userPaymentFlowType === "individual" || 
                      (userPaymentFlowType === null && userOrder?.paymentType === "individual");
  const isSplitEvenly = userPaymentFlowType === "split_evenly" ||
                       userOrder?.paymentType === "split_evenly" ||
                       orderData?.paymentType === "split_evenly" ||
                       orderData?.paymentFlow?.type === "split_evenly";
  const isPayForAll = !isIndividual && !isSplitEvenly && (
    userPaymentFlowType === "pay_for_all" ||
    userOrder?.paymentType === "pay_for_all" ||
    orderData?.paymentType === "pay_for_all" ||
    orderData?.paymentFlow?.type === "pay_for_all"
  );
  
  // Calculate tax breakdown (individual or group based on payment type)
  // This function will be used after userAmountToPay is calculated
  // Note: Taxes are already included in the baseAmount, so we need to extract subtotal before tax
  const calculateUserTaxBreakdown = (baseAmount) => {
    if (!orderData?.taxes || orderData.taxes.length === 0) {
      return {
        subtotalBeforeTax: baseAmount,
        taxBreakdown: [],
        totalTax: 0,
      };
    }

    // Calculate total tax rate (sum of all tax rates)
    let totalTaxRate = 0;
    orderData.taxes.forEach((taxGroup) => {
      taxGroup.taxRates.forEach((taxRate) => {
        if (taxRate.rate > 0) {
          totalTaxRate += taxRate.rate;
        }
      });
    });

    // Extract subtotal before tax from the amount that includes tax
    // Formula: subtotal = total / (1 + tax_rate/100)
    const subtotalBeforeTax = totalTaxRate > 0 
      ? Math.round((baseAmount / (1 + totalTaxRate / 100)) * 100) / 100
      : baseAmount;

    const taxBreakdown = [];
    let totalCalculatedTax = 0;

    // Calculate individual taxes on the subtotal before tax
    orderData.taxes.forEach((taxGroup) => {
      taxGroup.taxRates.forEach((taxRate) => {
        // Only include tax rates that are greater than 0
        if (taxRate.rate > 0) {
          // Calculate tax on the subtotal before tax and round to 2 decimal places
          const calculatedAmount = Math.round((subtotalBeforeTax * taxRate.rate / 100) * 100) / 100;
          taxBreakdown.push({
            type: taxRate.type.toUpperCase(),
            rate: taxRate.rate,
            amount: calculatedAmount,
          });
          totalCalculatedTax += calculatedAmount;
        }
      });
    });

    // Round total tax to 2 decimal places
    totalCalculatedTax = Math.round(totalCalculatedTax * 100) / 100;

    return {
      subtotalBeforeTax: Math.round(subtotalBeforeTax * 100) / 100,
      taxBreakdown: taxBreakdown,
      totalTax: totalCalculatedTax,
    };
  };

  // Use group totals for "pay for all", user's amountToPay for individual/split_evenly
  // For billing details, always show full order information (subtotal) for individual payments
  // Upfront payment and remaining amount info will be shown separately before pay button
  const isClosedOrder = orderData?.orderStatus === "closed order";
  const isUpfrontDone = upfrontPayment === 0;
  
  let userAmountToPay;
  if (isPayForAll) {
    userAmountToPay = orderData?.groupSubtotal || 0;
  } else if (isIndividual) {
    // For individual payments, always use full subtotal for billing display
    // This shows the actual order information, not the upfront amount
    userAmountToPay = userOrder?.subtotal || 0;
  } else if (isSplitEvenly) {
    // For split_evenly, pick the right amount based on payment status:
    // - paid:    show subtotal (their item cost) so billing reflects what they ordered
    // - pending with amountToPay>0: show amountToPay (their assigned split share)
    // - pending with amountToPay=0: show 0 (not part of the split, e.g. owner/member)
    if (userOrder?.paymentStatus === "paid") {
      userAmountToPay = userOrder?.subtotal || 0;
    } else {
      userAmountToPay = userOrder?.amountToPay || 0;
    }
  } else {
    // For other cases, use amountToPay or fallback to subtotal
    userAmountToPay = userOrder?.amountToPay > 0 ? userOrder.amountToPay : userOrder?.subtotal || 0;
  }
  
  // Calculate tax breakdown using the calculated userAmountToPay
  // Note: userAmountToPay already includes taxes, so we extract subtotal before tax
  const {
    subtotalBeforeTax: userSubtotalBeforeTax,
    taxBreakdown: userTaxBreakdown,
    totalTax: userTotalTax,
  } = calculateUserTaxBreakdown(userAmountToPay);
    
  // Grand total = userAmountToPay (already includes tax) + tip
  // Don't add tax again since it's already included in userAmountToPay
  const grandTotal = userAmountToPay ;

  // Check if payment flow has been initiated
  const hasPaymentFlowInitiated = () => {
    // Check order-level payment flow first
    if (orderData?.paymentFlow?.type !== null && orderData?.paymentFlow?.type !== undefined) {
      return true;
    }
    
    if (!orderData?.groupOrder?.userOrders) {
      return false;
    }
    
    // Get the current user's order
    const userOrder = orderData.groupOrder.userOrders.find(
      (userOrder) =>
        userOrder.userId?._id === currentUserId ||
        userOrder.userId?._id === storedUserId
    );
    
    // Check if current user has initiated a payment flow
    if (userOrder && 
        userOrder.paymentFlow?.type !== null && 
        userOrder.paymentFlow?.type !== undefined) {
      return true;
    }
    
    // If current user doesn't have a payment flow, check if any user has pay_for_all
    // For pay_for_all, all users should see the pay button (it's a group payment)
    const allUserOrders = orderData.groupOrder.userOrders || [];
    const anyUserHasPayForAll = allUserOrders.some((uo) => {
      return uo.paymentFlow?.type === "pay_for_all" || uo.paymentType === "pay_for_all";
    });
    
    if (anyUserHasPayForAll) {
      return true;
    }
    
    return false;
  };

  // Determine if payment button should be shown
  const shouldShowPaymentButton = () => {
    // For split_evenly payments, check user's individual payment status
    // even if group-level paymentCompleted is true
    if (isSplitEvenly && userOrder) {
      // Check if user has pending payment
      const userPaymentStatus = userOrder.paymentStatus;
      const userAmountToPay = userOrder.amountToPay || 0;
      const userAmountPaid = userOrder.amountPaid || 0;
      
      // Show button if user's payment status is pending and they have amount to pay
      if (userPaymentStatus === "pending" && userAmountToPay > userAmountPaid && remainingPayment > 0) {
        // Still need to check if payment flow is initiated
        if (hasPaymentFlowInitiated()) {
          return true;
        }
      }
    }

    // Don't show if payment is already completed (for non-split_evenly or if user has paid)
    if (orderData?.paymentCompleted) {
      return false;
    }

    // Don't show if no payment flow has been initiated
    if (!hasPaymentFlowInitiated()) {
      return false;
    }

    const orderStatus = orderData?.orderStatus;
    const upfrontPercentage = orderData?.paymentSettings?.upfrontPercentage || 0;

    // If upfront percentage is 0, no upfront payment needed
    if (upfrontPercentage === 0) {
      // Only show button when order is closed and there's remaining payment
      if (orderStatus === "closed order" && remainingPayment > 0) {
        return true;
      }
      return false;
    }

    // 1. Show if upfront amount is remaining
    if (upfrontPayment > 0) {
      return true;
    }

    // 2. If upfront is done (upfrontPayment === 0) and upfront percentage > 0,
    // show button only if order status is "closed order" and there's remaining payment
    if (upfrontPayment === 0 && upfrontPercentage > 0 && orderStatus === "closed order" && remainingPayment > 0) {
      return true;
    }

    return false;
  };

  const showPaymentButton = shouldShowPaymentButton();

  // Determine if payment completed banner should be shown
  // For split_evenly, only show if current user has also completed their payment
  const shouldShowPaymentCompletedBanner = () => {
    if (!orderData?.paymentCompleted) {
      return false;
    }
    
    // For split_evenly payments, check user's individual payment status
    if (isSplitEvenly && userOrder) {
      const userPaymentStatus = userOrder.paymentStatus;
      const userAmountToPay = userOrder.amountToPay || 0;
      const userAmountPaid = userOrder.amountPaid || 0;
      
      // Don't show banner if user's payment is still pending
      if (userPaymentStatus === "pending" && userAmountToPay > userAmountPaid) {
        return false;
      }
    }
    
    return true;
  };

  const showPaymentCompletedBanner = shouldShowPaymentCompletedBanner();

  // Calculate tax breakdown from taxes array
  const calculateTaxBreakdown = () => {
    if (!orderData?.taxes || orderData.taxes.length === 0) {
      return { subtotalBeforeTax: subtotal, taxBreakdown: [], totalTax: tax };
    }

    const taxBreakdown = [];
    let totalCalculatedTax = 0;

    orderData.taxes.forEach((taxGroup) => {
      taxGroup.taxRates.forEach((taxRate) => {
        // Only include tax rates that are greater than 0
        if (taxRate.rate > 0) {
          const calculatedAmount = (subtotal * taxRate.rate) / 100;
          taxBreakdown.push({
            type: taxRate.type.toUpperCase(),
            rate: taxRate.rate,
            amount: calculatedAmount,
          });
          totalCalculatedTax += calculatedAmount;
        }
      });
    });

    const subtotalBeforeTax = subtotal - totalCalculatedTax;

    return {
      subtotalBeforeTax: subtotalBeforeTax,
      taxBreakdown: taxBreakdown,
      totalTax: totalCalculatedTax,
    };
  };

  const { subtotalBeforeTax, taxBreakdown, totalTax } = calculateTaxBreakdown();

  // Format time ago
  const getTimeAgo = (dateString) => {
    return moment(dateString).fromNow();
  };

  // Helper function to get all sub-items (regular or variant)
  const getSubItems = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants.flatMap(
        (variant) =>
          variant.items?.map((variantItem) => ({
            ...variantItem,
            variantName: variant.variantName,
            variantNote: variant.note,
          })) || []
      );
    }
    return item.items || [];
  };

  // Helper function to get total quantity
  const getTotalQuantity = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants.reduce(
        (total, variant) =>
          total +
          (variant.items?.reduce(
            (variantTotal, variantItem) =>
              variantTotal + (variantItem.quantity || 0),
            0
          ) || 0),
        0
      );
    }
    return item.quantity || 0;
  };

  // Currency: order > location > empty (never hardcode USD/$)
  const locationCurrency = orderData?.locationId?.currency;
  const locationCurrencySymbol = orderData?.locationId?.currencySymbol;

  const getCurrencySymbol = (currencyCode, fallbackSymbol) => {
    if (!currencyCode || !currencyData?.currencies) return fallbackSymbol ?? "";
    const currency = currencyData.currencies.find(
      (curr) => curr.code === currencyCode.toUpperCase()
    );
    return currency ? currency.symbol : (fallbackSymbol ?? "");
  };

  const formatPrice = (price, currencyCode) => {
    const symbol = getCurrencySymbol(currencyCode || locationCurrency, locationCurrencySymbol);
    // Format price to 2 decimal places
    const formattedPrice = typeof price === 'number' ? price.toFixed(2) : parseFloat(price || 0).toFixed(2);
    return `${symbol}${formattedPrice}`;
  };

  // Helper function to get currency from order item
  const getOrderItemCurrency = (item) => {
    const { menuItemId, variantEnabled, variants, locationId } = item;
    
    // Extract locationId string (handle both object and string)
    const locationIdStr = locationId?._id || locationId || orderData?.locationId?._id;
    
    if (variantEnabled && variants && variants.length > 0) {
      // For variant items, get currency from the first variant's perLocationStock
      const firstVariant = variants[0];
      if (firstVariant.items && firstVariant.items.length > 0) {
        // Check if variant items have currency
        const itemCurrency = firstVariant.items[0].currency;
        if (itemCurrency) return itemCurrency;
      }
      
      // Fallback: get from menuItemId variants perLocationStock
      if (menuItemId.variants && menuItemId.variants.length > 0) {
        const variant = menuItemId.variants.find(
          (v) => v._id === firstVariant.items?.[0]?.variantId
        );
        if (variant?.perLocationStock && variant.perLocationStock.length > 0) {
          // Try to match locationId first, then use first available
          const locationStock = locationIdStr
            ? variant.perLocationStock.find(
                (stock) => stock.locationId === locationIdStr || stock.locationId?._id === locationIdStr
              )
            : variant.perLocationStock[0];
          if (locationStock?.currency) return locationStock.currency;
        }
      }
    } else {
      // For non-variant items, get currency from locationStock
      if (menuItemId.locationStock && menuItemId.locationStock.length > 0) {
        // Try to match locationId first, then use first available
        const locationStock = locationIdStr
          ? menuItemId.locationStock.find(
              (stock) => stock.locationId === locationIdStr || stock.locationId?._id === locationIdStr
            )
          : menuItemId.locationStock[0];
        if (locationStock?.currency) return locationStock.currency;
      }
    }
    
    // Fallback: order currency > location currency
    return orderData?.currency || locationCurrency || "";
  };

  // Helper function to get currency from all order items (for totals)
  const getOrderCurrency = () => {
    if (!orderData) return locationCurrency || "";
    
    // Get all items from the order
    const allOrderItems = orderData?.groupOrder?.userOrders || orderData?.cart || [];
    
    // Try to get currency from the first item
    for (const userOrder of allOrderItems) {
      const userCart = userOrder.cart || [];
      for (const item of userCart) {
        const itemCurrency = getOrderItemCurrency(item);
        if (itemCurrency) return itemCurrency;
      }
    }
    
    // Fallback: order currency > location currency
    return orderData?.currency || locationCurrency || "";
  };

  // Helper function to get price display with currency (returns unit price)
  const getPriceDisplay = (item) => {
    const itemCurrency = getOrderItemCurrency(item);
    
    if (item.variantEnabled && item.variants) {
      const prices = item.variants
        .flatMap(
          (variant) =>
            variant.items?.map((variantItem) => variantItem.variantPrice) || []
        )
        .filter((price) => price !== undefined);
      if (prices.length === 0) return formatPrice(0, itemCurrency);
      if (prices.length === 1) return formatPrice(prices[0], itemCurrency);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      return minPrice === maxPrice
        ? formatPrice(minPrice, itemCurrency)
        : `${formatPrice(minPrice, itemCurrency)}-${formatPrice(maxPrice, itemCurrency)}`;
    }
    // For non-variant items, itemPrice is the total price, so we need to calculate unit price
    // Unit price = total price / quantity
    const quantity = item.quantity || 1;
    let unitPrice = 0;
    
    // Try to get unit price from locationStock first (most accurate)
    if (item.menuItemId?.locationStock?.[0]?.price) {
      unitPrice = item.menuItemId.locationStock[0].price;
    } 
    // If itemPrice exists, calculate unit price from total
    else if (item.itemPrice && quantity > 0) {
      unitPrice = item.itemPrice / quantity;
    }
    // Fallback to menuItemId price
    else if (item.menuItemId?.price) {
      unitPrice = item.menuItemId.price;
    }
    
    return formatPrice(unitPrice, itemCurrency);
  };

  // Get background color based on item status
  const getStatusBackgroundColor = (status) => {
    const statusColorMap = {
      "new items": "#FFC4C4",
      "confirmed items": "#F0F0AD",
      "items being prepared": "#FFD1F1",
      "ready to serve": "#ADF0D2",
      "collected": "#ADF0D2",
      "ready to pick-up": "#ADF0D2",
      "ready to deliver": "#ADF0D2",
      "cancelled items": "#CFCFCF",
      "declined items": "#CFCFCF"
    };
    return statusColorMap[status?.toLowerCase()] || "#FFFFFF"; // Default to white if status not found
  };

  // Check if item should show expand arrow
  const shouldShowExpandArrow = (item) => {
    const subItems = getSubItems(item);
    return subItems.some((subItem) => {
      const hasSides =
        subItem.customizations?.sides &&
        subItem.customizations.sides.length > 0;
      const hasCustomizations =
        subItem.customizations?.ingredientModifications?.some(
          (mod) => mod.action !== "no_modification"
        );
      const hasNotes = subItem.variantNote && subItem.variantNote.trim() !== "";

      return hasSides || hasCustomizations || hasNotes;
    });
  };

  // Check if item has any customizations (for icon display in parent row)
  const hasItemCustomizations = (item) => {
    if (item.variantEnabled && item.variants) {
      // Check all variants for customizations
      return item.variants.some((variant) =>
        variant.items?.some((variantItem) =>
          variantItem.customizations?.ingredientModifications?.some(
            (mod) => mod.action !== "no_modification"
          )
        )
      );
    } else {
      // Check non-variant items for customizations
      return item.items?.some((subItem) =>
        subItem.customizations?.ingredientModifications?.some(
          (mod) => mod.action !== "no_modification"
        )
      );
    }
  };

  // Check if item has any notes (for icon display in parent row)
  const hasItemNotes = (item) => {
    if (item.variantEnabled && item.variants) {
      // Check all variants for notes
      return item.variants.some(
        (variant) => variant.note && variant.note.trim() !== ""
      );
    } else {
      // Check item-level note
      return item.note && item.note.trim() !== "";
    }
  };

  // Get all notes for tooltip
  const getAllNotes = (item) => {
    if (item.variantEnabled && item.variants) {
      return item.variants
        .filter((variant) => variant.note && variant.note.trim() !== "")
        .map((variant) => `${variant.variantName}: ${variant.note}`)
        .join(", ");
    } else {
      return item.note || "";
    }
  };

  // Helper function to check if an item is cancelled (with optional variant)
  // ONLY returns true for approved or pending_approval cancellations
  // Rejected cancellations are treated as if nothing happened
  const isItemCancelled = (item, variantName = null) => {
    const cancellationKey = variantName
      ? `${item._id}-${variantName}`
      : item._id;

    // Check local pending cancellations first
    if (cancelledItems[cancellationKey]) {
      return true;
    }
    
    // Check API cancellations - ONLY approved or pending_approval
    if (item.cancellations && item.cancellations.length > 0) {
      if (variantName) {
        return item.cancellations.some(c => 
          c.variantName === variantName && 
          c.requested && 
          (c.status === "approved" || c.status === "pending_approval")
        );
      } else {
        return item.cancellations.some(c => 
          c.requested && 
          !c.variantName && 
          (c.status === "approved" || c.status === "pending_approval")
        );
      }
    }
    
    // Check item.cancellation field - ONLY approved or pending_approval
    return item.cancellation?.requested && 
      (item.cancellation?.status === "approved" || item.cancellation?.status === "pending_approval");
  };

  // Helper function to get cancelled item IDs for strikethrough
  // ONLY returns items with status "approved" or "pending_approval"
  // Items with status "rejected" are NOT included (treated as if nothing happened)
  const getCancelledItemNumbers = (item, variantName = null) => {
    const cancellationKey = variantName ? `${item._id}-${variantName}` : item._id;
    
    // Collect all cancelled item numbers from different sources
    let allCancelledItems = [];
    
    // 1. Get local pending customer cancellations
    if (cancelledItems[cancellationKey]) {
      allCancelledItems = [...(cancelledItems[cancellationKey].itemIds || [])];
    }
    
    // 2. Get API cancellations (approved or pending_approval ONLY) and merge
    if (item.cancellations && item.cancellations.length > 0) {
      if (variantName) {
        // ONLY include approved or pending_approval cancellations (NOT rejected)
        const variantCancellations = item.cancellations.filter(c => 
          c.variantName === variantName && 
          c.requested && 
          (c.status === "approved" || c.status === "pending_approval")
        );
        variantCancellations.forEach(cancellation => {
          if (cancellation.itemIds) {
            allCancelledItems = [...allCancelledItems, ...cancellation.itemIds];
          }
        });
      } else {
        // ONLY include approved or pending_approval cancellations (NOT rejected)
        const nonVariantCancellations = item.cancellations.filter(c => 
          c.requested && 
          !c.variantName && 
          (c.status === "approved" || c.status === "pending_approval")
        );
        nonVariantCancellations.forEach(cancellation => {
          if (cancellation.itemIds) {
            allCancelledItems = [...allCancelledItems, ...cancellation.itemIds];
          }
        });
      }
    }
    
    // 3. Also check item.cancellation field (legacy support) - ONLY approved or pending_approval
    if (item.cancellation?.requested && 
        item.cancellation?.itemIds &&
        (item.cancellation?.status === "approved" || item.cancellation?.status === "pending_approval")) {
      allCancelledItems = [...allCancelledItems, ...item.cancellation.itemIds];
    }
    
    // 4. For variant items, also check individual item cancellations - ONLY approved or pending_approval
    if (item.variantEnabled && item.variants && variantName) {
      const variant = item.variants.find(v => v.variantName === variantName);
      if (variant && variant.items) {
        variant.items.forEach(variantItem => {
          if (variantItem.cancellation?.requested && 
              variantItem._id &&
              (variantItem.cancellation?.status === "approved" || variantItem.cancellation?.status === "pending_approval")) {
            allCancelledItems.push(variantItem._id);
          }
        });
      }
    } else if (item.items) {
      // For non-variant items, check individual item cancellations - ONLY approved or pending_approval
      item.items.forEach(subItem => {
        if (subItem.cancellation?.requested && 
            subItem._id &&
            (subItem.cancellation?.status === "approved" || subItem.cancellation?.status === "pending_approval")) {
          allCancelledItems.push(subItem._id);
        }
      });
    }
    
    // Remove duplicates and return
    return [...new Set(allCancelledItems)];
  };

  const getCancellationStatusForItem = (item, itemId, variantName = null) => {
    if (!item) return null;

    // Check local pending cancellations first (selection mode)
    const cancellationKey = variantName
      ? `${item._id}-${variantName}`
      : item._id;
    const localPending = cancelledItems[cancellationKey];
    if (localPending?.itemIds?.includes(itemId)) {
      return "pending_approval";
    }

    // Fallback to API status
    if (variantName && item.variantEnabled && item.variants) {
      const variant = item.variants.find((v) => v.variantName === variantName);
      const variantItem = variant?.items?.find((vi) => vi._id === itemId);
      return variantItem?.cancellation?.status || null;
    }

    const subItem = item.items?.find((si) => si._id === itemId);
    return subItem?.cancellation?.status || null;
  };

  // Reusable component for rendering sub-items for a specific variant
  const renderVariantSubItems = (
    variant,
    variantKey,
    isSelectionMode,
    cancelledItemIds = [],
    item = null
  ) => {
    const variantItems = variant.items || [];

    // Filter out cancelled items - only show remaining items
    const remainingItems = variantItems.filter(
      (variantItem) => !cancelledItemIds.includes(variantItem._id)
    );

    return remainingItems.map((variantItem, itemIndex) => {
      const subItemKey = `${variantKey}-item-${itemIndex}`;
      const hasSides =
        variantItem.customizations?.sides &&
        variantItem.customizations.sides.length > 0;
      const hasCustomizations =
        variantItem.customizations?.ingredientModifications?.some(
          (mod) => mod.action !== "no_modification"
        );

      return (
        <TableRow key={subItemKey} sx={{ backgroundColor: "#f9f9f9" }}>
          {isSelectionMode && <TableCell></TableCell>}
          <TableCell></TableCell>
          <TableCell sx={{ pl: 4 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "lowercase" }}
                >
                  item {variantItem.itemNumber}
                </Typography>
                {hasCustomizations && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            textTransform: "lowercase",
                          }}
                        >
                          customizations:
                        </Typography>
                        {variantItem.customizations.ingredientModifications
                          .filter((mod) => mod.action !== "no_modification")
                          .map((mod, modIndex) => {
                            let text = "";
                            if (mod.action === "substitute") {
                              text = `Substitute ${mod.ingredientName} with ${mod.substituteWith}`;
                            } else if (mod.action === "remove") {
                              text = `Remove ${mod.ingredientName}`;
                            } else if (mod.action === "add") {
                              text = `Add extra ${mod.ingredientName}`;
                            }
                            return (
                              <Typography
                                key={modIndex}
                                variant="caption"
                                sx={{
                                  display: "block",
                                  textTransform: "lowercase",
                                  mb: 0.5,
                                }}
                              >
                                • {text}
                              </Typography>
                            );
                          })}
                      </Box>
                    }
                    arrow
                    placement="top"
                    PopperProps={{
                      style: { zIndex: 10001 },
                    }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "white",
                          color: "black",
                          border: "1px solid #e0e0e0",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          "& .MuiTooltip-arrow": {
                            color: "white",
                            "&::before": {
                              border: "1px solid #e0e0e0",
                            },
                          },
                        },
                      },
                    }}
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <CustomizeIcon size="16" color="#6200EE" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {hasSides && (
                <Box sx={{ mt: 1 }}>
                  {variantItem.customizations.sides.map((side, sideIndex) => {
                    const sideCurrency = side.currency || (item ? getOrderItemCurrency(item) : getOrderCurrency());
                    return (
                      <Typography
                        key={sideIndex}
                        variant="caption"
                        sx={{
                          display: "block",
                          textTransform: "lowercase",
                          mb: 0.5,
                          pl: 2,
                        }}
                      >
                        {side.sideName} + {formatPrice(side.price, sideCurrency)}
                      </Typography>
                    );
                  })}
                </Box>
              )}
            </Box>
          </TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
        </TableRow>
      );
    });
  };

  // Reusable component for rendering sub-items (for non-variant items)
  const renderSubItems = (
    item,
    itemKey,
    isSelectionMode,
    cancelledItemIds = []
  ) => {
    const subItems = getSubItems(item);

    // Filter out cancelled items - only show remaining items
    const remainingItems = subItems.filter(
      (subItem) => !cancelledItemIds.includes(subItem._id)
    );

    return remainingItems.map((subItem, subIndex) => {
      const subItemKey = `${itemKey}-${subIndex}`;
      const hasSides =
        subItem.customizations?.sides &&
        subItem.customizations.sides.length > 0;
      const hasCustomizations =
        subItem.customizations?.ingredientModifications?.some(
          (mod) => mod.action !== "no_modification"
        );

      return (
        <TableRow key={subItemKey} sx={{ backgroundColor: "#f9f9f9" }}>
          {isSelectionMode && <TableCell></TableCell>}
          <TableCell></TableCell>
          <TableCell sx={{ pl: 4 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "lowercase" }}
                >
                  {subItem.variantName
                    ? `${subItem.variantName} variant`
                    : `item ${subItem.itemNumber || subIndex + 1}`}
                  {subItem.variantName &&
                    ` • qty: ${subItem.quantity} • ${formatPrice(subItem.variantPrice, subItem.currency || getOrderItemCurrency(item))} each`}
                </Typography>
                {subItem.variantNote && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "lowercase", display: "block", mb: 1 }}
                  >
                    note: {subItem.variantNote}
                  </Typography>
                )}
                {hasCustomizations && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            textTransform: "lowercase",
                          }}
                        >
                          customizations:
                        </Typography>
                        {subItem.customizations.ingredientModifications
                          .filter((mod) => mod.action !== "no_modification")
                          .map((mod, modIndex) => {
                            let text = "";
                            if (mod.action === "substitute") {
                              text = `Substitute ${mod.ingredientName} with ${mod.substituteWith}`;
                            } else if (mod.action === "remove") {
                              text = `Remove ${mod.ingredientName}`;
                            } else if (mod.action === "add") {
                              text = `Add extra ${mod.ingredientName}`;
                            }
                            return (
                              <Typography
                                key={modIndex}
                                variant="caption"
                                sx={{
                                  display: "block",
                                  textTransform: "lowercase",
                                  mb: 0.5,
                                }}
                              >
                                • {text}
                              </Typography>
                            );
                          })}
                      </Box>
                    }
                    arrow
                    placement="top"
                    PopperProps={{ style: { zIndex: 10001 } }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "white",
                          color: "black",
                          border: "1px solid #e0e0e0",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          "& .MuiTooltip-arrow": {
                            color: "white",
                            "&::before": { border: "1px solid #e0e0e0" },
                          },
                        },
                      },
                    }}
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <CustomizeIcon size="16" color="#6200EE" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {hasSides && (
                <Box sx={{ mt: 1 }}>
                  {subItem.customizations.sides.map((side, sideIndex) => {
                    const sideCurrency = side.currency || getOrderItemCurrency(item);
                    return (
                      <Typography
                        key={sideIndex}
                        variant="caption"
                        sx={{
                          display: "block",
                          textTransform: "lowercase",
                          mb: 0.5,
                          pl: 2,
                        }}
                      >
                        {side.sideName} + {formatPrice(side.price, sideCurrency)}
                        {side.quantity > 1 ? ` (qty: ${side.quantity})` : ""}
                      </Typography>
                    );
                  })}
                </Box>
              )}
            </Box>
          </TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
        </TableRow>
      );
    });
  };

  // Render order items table for Order Details tab
  const renderOrderItems = () => {
    // Check for group order data structure
    const allOrderItems =
      orderData?.groupOrder?.userOrders || orderData?.cart || [];

    // Filter items based on search term
    const orderItems = filterOrderItems(allOrderItems);

    if (orderItems.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ p: 2, textTransform: "lowercase" }}
        >
          {searchTerm.trim()
            ? "no items found matching your search"
            : "no items in this order"}
        </Typography>
      );
    }

    return (
      <ColoredTable component={Paper}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                textTransform: "lowercase",
                backgroundColor: "#E4E4E4 !important",
              }}
            >
              {isSelectionMode && (
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      checked={
                        selectedItems.size > 0 &&
                        selectedItems.size === getTotalItemCount()
                      }
                      indeterminate={
                        selectedItems.size > 0 &&
                        selectedItems.size < getTotalItemCount()
                      }
                      onChange={
                        selectedItems.size === getTotalItemCount()
                          ? handleDeselectAll
                          : handleSelectAll
                      }
                      size="small"
                      sx={{ p: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "lowercase" }}
                    >
                      {selectedItems.size === getTotalItemCount()
                        ? "deselect all"
                        : "select all"}
                    </Typography>
                  </Box>
                </TableCell>
              )}
              <TableCell>item code</TableCell>
              <TableCell>items</TableCell>
              <TableCell>variant</TableCell>
              <TableCell>qty</TableCell>
              <TableCell>notes/custom</TableCell>
              <TableCell>customer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderItems.map((userOrder, userIndex) => {
              const userCart = userOrder.cart || [];
              return userCart.map((item, index) => {
                const itemKey = `${userIndex}-${index}`;
                const shouldExpand = shouldShowExpandArrow(item);
                const isExpanded = expandedSides[itemKey];

                // For variant-enabled items, show each variant as a separate row
                if (item.variantEnabled && item.variants) {
                  return item.variants.map((variant, variantIndex) => {
                    const variantQuantity = variant.items?.reduce((total, variantItem) => total + (variantItem.quantity || 0), 0) || 0;
                    const variantPrice = variant.items?.[0]?.variantPrice || 0;
                    const variantKey = `${itemKey}-${variantIndex}`;
                    const isCancelled = isItemCancelled(item, variant.variantName);
                    const cancelledItemIds = getCancelledItemNumbers(item, variant.variantName);
                    
                    // Only show variants that have items
                    if (variantQuantity === 0) return null;
                    
                    // Calculate how many items to show as cancelled for this variant
                    const variantCancelledQuantity = cancelledItemIds.length;
                    const remainingQuantity = variantQuantity - variantCancelledQuantity;
                    
                    const rows = [];
                    
                    // Show remaining quantity row
                    if (remainingQuantity > 0) {
                      const currentStatus = variant.itemStatus || item.itemStatus || "new items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      rows.push(
                        <TableRow key={variantKey} sx={{ backgroundColor }}>
                          {isSelectionMode && (
                            <TableCell>
                              <Checkbox
                                checked={selectedItems.has(variantKey)}
                                onChange={() => handleItemSelect(variantKey)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                                {item.menuItemId?.name} - {variant.variantName} variant
                              </Typography>
                              <Typography variant="caption" color="error">
                                *
                              </Typography>
                              {shouldExpand && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleSidesExpansion(variantKey)}
                                  sx={{ p: 0.5 }}
                                >
                                  {expandedSides[variantKey] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {variant.variantName}
                          </TableCell>
                          <TableCell>
                            {isSelectionMode ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenCancellationDialog(item, userIndex, index, 'decrease', variant.variantName)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: "#FEF2F2",
                                  color: "#DC2626",
                                  "&:hover": {
                                    backgroundColor: "#DC2626",
                                    color: "white",
                                  },
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="body2" sx={{ minWidth: 20, textAlign: "center" }}>
                                {remainingQuantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenCancellationDialog(item, userIndex, index, 'increase', variant.variantName)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: "#F0F9FF",
                                  color: "#0369A1",
                                  "&:hover": {
                                    backgroundColor: "#0369A1",
                                    color: "white",
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                              </Box>
                            ) : (
                              <Typography variant="body2">
                                {remainingQuantity}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {hasItemNotes(item) && (
                              <Tooltip 
                                  title={getAllNotes(item)} 
                                arrow
                                placement="top"
                                PopperProps={{
                                  style: { zIndex: 10001 }
                                }}
                              >
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <NotesIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                              )}
                              {hasItemCustomizations(item) && (
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <CustomizeIcon size="16" color="#6200EE" />
                                </IconButton>
                              )}
                              {!hasItemNotes(item) && !hasItemCustomizations(item) && (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar 
                                src={getImageDisplayUrl(userOrder.userProfilePic)}
                                sx={{ width: 24, height: 24 }}
                              >
                                {userOrder.userFullName?.charAt(0) || "U"}
                              </Avatar>
                              <Typography variant="caption" sx={{ textTransform: "lowercase" }}>
                                {userOrder.userFullName || "user"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    // Show cancelled variant - consolidated row when all quantities are cancelled
                    if (variantCancelledQuantity > 0 && cancelledItemIds.length > 0) {
                      // Check if ALL quantities of this variant are cancelled
                      const isFullyCancelled = variantCancelledQuantity === variant.items?.length;
                      
                      if (isFullyCancelled) {
                        // Show ONE consolidated row for fully cancelled variant
                        const currentStatus = variant.itemStatus || item.itemStatus || "cancelled items";
                        const backgroundColor = getStatusBackgroundColor(currentStatus);
                      const cancelledLabel = cancelledItemIds.some(
                        (itemId) =>
                          getCancellationStatusForItem(
                            item,
                            itemId,
                            variant.variantName
                          ) === "pending_approval"
                      )
                        ? "pending cancellation"
                        : "cancelled";
                        rows.push(
                          <TableRow key={`${variantKey}-cancelled-full`} sx={{ opacity: 0.6, backgroundColor }}>
                            {isSelectionMode && (
                              <TableCell>
                                {selectedItems.has(`${variantKey}`) && (
                                  <Checkbox
                                    checked={true}
                                    onChange={() => handleItemSelect(`${variantKey}`)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                  />
                                )}
                              </TableCell>
                            )}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary"
                                  }}
                                >
                                  {item.menuItemId?.name} - {variant.variantName} variant (all quantities {cancelledLabel})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textDecoration: "line-through",
                                  color: "text.secondary"
                                }}
                              >
                                {variantCancelledQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar 
                                  src={getImageDisplayUrl(userOrder.userProfilePic)}
                                  sx={{ width: 24, height: 24, opacity: 0.6 }}
                                >
                                  {userOrder.userFullName?.charAt(0) || "U"}
                                </Avatar>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary"
                                  }}
                                >
                                  {userOrder.userFullName || "user"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // Show individual cancelled items for partial cancellation
                        const currentStatus = variant.itemStatus || item.itemStatus || "cancelled items";
                        const backgroundColor = getStatusBackgroundColor(currentStatus);
                        cancelledItemIds.forEach((itemId) => {
                          const itemLabel =
                            getCancellationStatusForItem(
                              item,
                              itemId,
                              variant.variantName
                            ) === "pending_approval"
                              ? "pending cancellation"
                              : "cancelled";
                          rows.push(
                            <TableRow key={`${variantKey}-cancelled-${itemId}`} sx={{ opacity: 0.6, backgroundColor }}>
                            {isSelectionMode && <TableCell></TableCell>}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary"
                                  }}
                                >
                                    {item.menuItemId?.name} - {variant.variantName} variant ({itemLabel})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textDecoration: "line-through",
                                  color: "text.secondary"
                                }}
                              >
                                1
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar 
                                  src={getImageDisplayUrl(userOrder.userProfilePic)}
                                  sx={{ width: 24, height: 24, opacity: 0.6 }}
                                >
                                  {userOrder.userFullName?.charAt(0) || "U"}
                                </Avatar>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary"
                                  }}
                                >
                                  {userOrder.userFullName || "user"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                        });
                      }
                    }
                    
                    // Render sub-items if expanded
                    if (shouldExpand && expandedSides[variantKey]) {
                      rows.push(...renderVariantSubItems(variant, variantKey, isSelectionMode, cancelledItemIds, item));
                    }
                    
                    return rows;
                  }).filter(Boolean).flat(); // Remove null entries and flatten
                } else {
                  // For regular items, check for cancellations
                  const isCancelled = isItemCancelled(item);
                  const cancelledItemIds = getCancelledItemNumbers(item);
                  const totalQuantity = getTotalQuantity(item);
                  const cancelledQuantity = cancelledItemIds.length;
                  const remainingQuantity = totalQuantity - cancelledQuantity;

                  const rows = [];

                  // Show remaining quantity row
                  if (remainingQuantity > 0) {
                    const currentStatus = item.itemStatus || "new items";
                    const backgroundColor = getStatusBackgroundColor(currentStatus);
                    rows.push(
                      <TableRow key={itemKey} sx={{ backgroundColor }}>
                        {isSelectionMode && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.has(itemKey)}
                              onChange={() => handleItemSelect(itemKey)}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          {item.menuItemId?.code ||
                            `i${String(index + 1).padStart(3, "0")}`}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ textTransform: "lowercase" }}
                            >
                              {item.menuItemId?.name}
                            </Typography>
                            <Typography variant="caption" color="error">
                              *
                            </Typography>
                            {shouldExpand && (
                              <IconButton
                                size="small"
                                onClick={() => toggleSidesExpansion(itemKey)}
                                sx={{ p: 0.5 }}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          standard
                        </TableCell>
                        <TableCell>
                          {isSelectionMode ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenCancellationDialog(
                                    item,
                                    userIndex,
                                    index,
                                    "decrease"
                                  )
                                }
                                sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: "#FEF2F2",
                                  color: "#DC2626",
                                  "&:hover": {
                                    backgroundColor: "#DC2626",
                                    color: "white",
                                  },
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography
                                variant="body2"
                                sx={{ minWidth: 20, textAlign: "center" }}
                              >
                                {remainingQuantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenCancellationDialog(
                                    item,
                                    userIndex,
                                    index,
                                    "increase"
                                  )
                                }
                                sx={{
                                  width: 24,
                                  height: 24,
                                  backgroundColor: "#F0F9FF",
                                  color: "#0369A1",
                                  "&:hover": {
                                    backgroundColor: "#0369A1",
                                    color: "white",
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2">
                              {remainingQuantity}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {hasItemNotes(item) && (
                              <Tooltip
                                title={getAllNotes(item)}
                                arrow
                                placement="top"
                                PopperProps={{
                                  style: { zIndex: 10001 },
                                }}
                              >
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <NotesIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasItemCustomizations(item) && (
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                <CustomizeIcon size="16" color="#6200EE" />
                              </IconButton>
                            )}
                            {!hasItemNotes(item) &&
                              !hasItemCustomizations(item) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              src={getImageDisplayUrl(userOrder.userProfilePic)}
                              sx={{ width: 24, height: 24 }}
                            >
                              {userOrder.userFullName?.charAt(0) || "U"}
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{ textTransform: "lowercase" }}
                            >
                              {userOrder.userFullName || "user"}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Show cancelled item - consolidated row when all quantities are cancelled
                  if (
                    cancelledQuantity > 0 &&
                    cancelledItemIds.length > 0
                  ) {
                    // Check if ALL quantities of this item are cancelled
                    const isFullyCancelled =
                      cancelledQuantity === totalQuantity;

                    if (isFullyCancelled) {
                      // Show ONE consolidated row for fully cancelled item
                      const currentStatus = item.itemStatus || "cancelled items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      const cancelledLabel = cancelledItemIds.some(
                        (itemId) =>
                          getCancellationStatusForItem(item, itemId, null) ===
                          "pending_approval"
                      )
                        ? "pending cancellation"
                        : "cancelled";
                      rows.push(
                        <TableRow
                          key={`${itemKey}-cancelled-full`}
                          sx={{ opacity: 0.6, backgroundColor }}
                        >
                          {isSelectionMode && (
                            <TableCell>
                              {selectedItems.has(itemKey) && (
                                <Checkbox
                                  checked={true}
                                  onChange={() => handleItemSelect(itemKey)}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                  />
                              )}
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code ||
                              `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  textTransform: "lowercase",
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                }}
                              >
                                {item.menuItemId?.name} (all quantities {cancelledLabel})
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography
                              sx={{
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              standard
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              {cancelledQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              -
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                src={getImageDisplayUrl(userOrder.userProfilePic)}
                                sx={{ width: 24, height: 24, opacity: 0.6 }}
                              >
                                {userOrder.userFullName?.charAt(0) || "U"}
                              </Avatar>
                              <Typography
                                variant="caption"
                                sx={{
                                  textTransform: "lowercase",
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                }}
                              >
                                {userOrder.userFullName || "user"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // Show individual cancelled items for partial cancellation
                      const currentStatus = item.itemStatus || "cancelled items";
                      const backgroundColor = getStatusBackgroundColor(currentStatus);
                      cancelledItemIds.forEach((itemId) => {
                        const itemLabel =
                          getCancellationStatusForItem(item, itemId, null) ===
                          "pending_approval"
                            ? "pending cancellation"
                            : "cancelled";
                        rows.push(
                          <TableRow key={`${itemKey}-cancelled-${itemId}`} sx={{ opacity: 0.6, backgroundColor }}>
                            {isSelectionMode && <TableCell></TableCell>}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code ||
                                `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary",
                                  }}
                                >
                                  {item.menuItemId?.name} ({itemLabel})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography
                                sx={{
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                }}
                              >
                                standard
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                }}
                              >
                                1
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  src={getImageDisplayUrl(userOrder.userProfilePic)}
                                  sx={{ width: 24, height: 24, opacity: 0.6 }}
                                >
                                  {userOrder.userFullName?.charAt(0) || "U"}
                                </Avatar>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary",
                                  }}
                                >
                                  {userOrder.userFullName || "user"}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    }
                  }

                  // Render item details with sides if expanded
                  if (shouldExpand && isExpanded && remainingQuantity > 0) {
                    rows.push(
                      ...renderSubItems(
                        item,
                        `${userIndex}-${index}`,
                        isSelectionMode,
                        cancelledItemIds
                      )
                    );
                  }

                  return <React.Fragment key={itemKey}>{rows}</React.Fragment>;
                }
              });
            })}
          </TableBody>
        </Table>
      </ColoredTable>
    );
  };

  // Render billing items table for Billing Details tab
  const renderBillingItems = () => {
    // Check for group order data structure
    const allOrderItems =
      orderData?.groupOrder?.userOrders || orderData?.cart || [];

    // Filter items based on search term
    const orderItems = filterOrderItems(allOrderItems);

    if (orderItems.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ p: 2, textTransform: "lowercase" }}
        >
          {searchTerm.trim()
            ? "no items found matching your search"
            : "no items in this order"}
        </Typography>
      );
    }

    // Flatten all items from all users into a single array
    const allItems = [];
    orderItems.forEach((userOrder, userIndex) => {
      const userCart = userOrder.cart || [];
      userCart.forEach((item, itemIndex) => {
        allItems.push({
          ...item,
          userIndex,
          itemIndex,
          userOrder,
          itemKey: `${userIndex}-${itemIndex}`,
        });
      });
    });

    return (
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 2,
          border: "1px solid #e5e7eb",
          borderRadius: 1,
          "& .MuiTableCell-root": {
            padding: "4px !important",
          },
          "& .MuiTableCell-head": {
            padding: "4px !important",
          },
          "& .MuiTableCell-body": {
            padding: "4px !important",
          },
          "& table .MuiTableCell-root": {
            padding: "4px !important",
          },
          "& .MuiTable-root .MuiTableCell-root": {
            padding: "4px !important",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                textTransform: "lowercase",
                backgroundColor: "#E4E4E4 !important",
              }}
            >
              {isSelectionMode && (
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      checked={
                        selectedItems.size > 0 &&
                        selectedItems.size === allItems.length
                      }
                      indeterminate={
                        selectedItems.size > 0 &&
                        selectedItems.size < allItems.length
                      }
                      onChange={
                        selectedItems.size === allItems.length
                          ? handleDeselectAll
                          : handleSelectAll
                      }
                      size="small"
                      sx={{ p: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "lowercase" }}
                    >
                      {selectedItems.size === allItems.length
                        ? "deselect all"
                        : "select all"}
                    </Typography>
                  </Box>
                </TableCell>
              )}
              <TableCell>item code</TableCell>
              <TableCell>item details</TableCell>
              <TableCell>variant</TableCell>
              <TableCell>notes/custom</TableCell>
              <TableCell>customer</TableCell>
              <TableCell>qty</TableCell>
              <TableCell>unit price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allItems.map((item, index) => {
              const shouldExpand = shouldShowExpandArrow(item);
              const isExpanded = expandedSides[item.itemKey];

              // For variant-enabled items, show each variant as a separate row
              if (item.variantEnabled && item.variants) {
                return item.variants.map((variant, variantIndex) => {
                  const variantQuantity = variant.items?.reduce((total, variantItem) => total + (variantItem.quantity || 0), 0) || 0;
                  const variantPrice = variant.items?.[0]?.variantPrice || 0;
                  const variantKey = `${item.itemKey}-${variantIndex}`;
                  const isCancelled = isItemCancelled(item, variant.variantName);
                  const cancelledItemIds = getCancelledItemNumbers(item, variant.variantName);
                  
                  // Only show variants that have items
                  if (variantQuantity === 0) return null;
                  
                  // Calculate how many items to show as cancelled for this variant
                  const variantCancelledQuantity = cancelledItemIds.length;
                  const remainingQuantity = variantQuantity - variantCancelledQuantity;
                  
                  const rows = [];
                  
                  // Show remaining quantity row
                  if (remainingQuantity > 0) {
                    rows.push(
                    <TableRow key={variantKey}>
                      {isSelectionMode && (
                        <TableCell>
                          <Checkbox
                            checked={selectedItems.has(variantKey)}
                            onChange={() => handleItemSelect(variantKey)}
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ textTransform: "lowercase" }}>
                        {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.name} - {variant.variantName} variant
                          </Typography>
                          <Typography variant="caption" color="error">
                            *
                          </Typography>
                          {shouldExpand && (
                            <IconButton
                              size="small"
                              onClick={() => toggleSidesExpansion(variantKey)}
                              sx={{ p: 0.5 }}
                            >
                              {expandedSides[variantKey] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textTransform: "lowercase" }}>
                        {variant.variantName}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {hasItemNotes(item) && (
                            <Tooltip 
                              title={getAllNotes(item)} 
                              arrow
                              placement="top"
                              PopperProps={{
                                style: { zIndex: 10001 }
                              }}
                            >
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                <NotesIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasItemCustomizations(item) && (
                            <IconButton size="small" sx={{ p: 0.5 }}>
                              <CustomizeIcon size="16" color="#6200EE" />
                            </IconButton>
                          )}
                          {!hasItemNotes(item) && !hasItemCustomizations(item) && (
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar 
                            src={getImageDisplayUrl(item.userOrder.userProfilePic)}
                            sx={{ width: 24, height: 24 }}
                          >
                            {item.userOrder.userFullName?.charAt(0) || "U"}
                          </Avatar>
                          <Typography variant="caption" sx={{ textTransform: "lowercase" }}>
                            {item.userOrder.userFullName || "user"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isSelectionMode ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCancellationDialog(item, item.userIndex, item.itemIndex, 'decrease', variant.variantName)}
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: "#FEF2F2",
                                color: "#DC2626",
                                "&:hover": {
                                  backgroundColor: "#DC2626",
                                  color: "white",
                                },
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 20, textAlign: "center" }}>
                              {remainingQuantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCancellationDialog(item, item.userIndex, item.itemIndex, 'increase', variant.variantName)}
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: "#F0F9FF",
                                color: "#0369A1",
                                "&:hover": {
                                  backgroundColor: "#0369A1",
                                  color: "white",
                                },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {remainingQuantity}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                          <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                          {formatPrice(variantPrice, variant.items?.[0]?.currency || getOrderItemCurrency(item))} / per variant
                          </Typography>
                      </TableCell>
                    </TableRow>
                  );
                  }
                  
                  // Show cancelled variant - consolidated row when all quantities are cancelled
                  if (variantCancelledQuantity > 0 && cancelledItemIds.length > 0) {
                    // Check if ALL quantities of this variant are cancelled
                    const isFullyCancelled = variantCancelledQuantity === variant.items?.length;
                    
                    if (isFullyCancelled) {
                      // Show ONE consolidated row for fully cancelled variant
                      const cancelledLabel = cancelledItemIds.some(
                        (itemId) =>
                          getCancellationStatusForItem(
                            item,
                            itemId,
                            variant.variantName
                          ) === "pending_approval"
                      )
                        ? "pending cancellation"
                        : "cancelled";
                      rows.push(
                        <TableRow key={`${variantKey}-cancelled-full`} sx={{ opacity: 0.6 }}>
                          {isSelectionMode && (
                            <TableCell>
                              {selectedItems.has(variantKey) && (
                                <Checkbox
                                  checked={true}
                                  onChange={() => handleItemSelect(variantKey)}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textTransform: "lowercase",
                                  textDecoration: "line-through",
                                  color: "text.secondary"
                                }}
                              >
                                {item.menuItemId?.name} - {variant.variantName} variant (all quantities {cancelledLabel})
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                              {variant.variantName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar 
                                src={getImageDisplayUrl(item.userOrder.userProfilePic)}
                                sx={{ width: 24, height: 24, opacity: 0.6 }}
                              >
                                {item.userOrder.userFullName?.charAt(0) || "U"}
                              </Avatar>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textTransform: "lowercase",
                                  textDecoration: "line-through",
                                  color: "text.secondary"
                                }}
                              >
                                {item.userOrder.userFullName || "user"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                textDecoration: "line-through",
                                color: "text.secondary"
                              }}
                            >
                              {variantCancelledQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: "lowercase", textDecoration: "line-through", color: "text.secondary" }}>
                              {formatPrice(variantPrice, variant.items?.[0]?.currency || getOrderItemCurrency(item))} / per variant
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
              } else {
                      // Show individual cancelled items for partial cancellation
                      cancelledItemIds.forEach((itemId) => {
                        const itemLabel =
                          getCancellationStatusForItem(
                            item,
                            itemId,
                            variant.variantName
                          ) === "pending_approval"
                            ? "pending cancellation"
                            : "cancelled";
                        rows.push(
                          <TableRow key={`${variantKey}-cancelled-${itemId}`} sx={{ opacity: 0.6 }}>
                            {isSelectionMode && <TableCell></TableCell>}
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              {item.menuItemId?.code || `i${String(index + 1).padStart(3, "0")}`}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary"
                                  }}
                                >
                                  {item.menuItemId?.name} - {variant.variantName} variant ({itemLabel})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textTransform: "lowercase" }}>
                              <Typography sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {variant.variantName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar 
                                  src={getImageDisplayUrl(item.userOrder.userProfilePic)}
                                  sx={{ width: 24, height: 24, opacity: 0.6 }}
                                >
                                  {item.userOrder.userFullName?.charAt(0) || "U"}
                                </Avatar>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    textTransform: "lowercase",
                                    textDecoration: "line-through",
                                    color: "text.secondary"
                                  }}
                                >
                                  {item.userOrder.userFullName || "user"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textDecoration: "line-through",
                                  color: "text.secondary"
                                }}
                              >
                                1
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: "lowercase", textDecoration: "line-through", color: "text.secondary" }}>
                                {formatPrice(variantPrice, variant.items?.[0]?.currency || getOrderItemCurrency(item))} / per variant
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    }
                  }
                  
                  // Render sub-items if expanded
                  if (shouldExpand && expandedSides[variantKey]) {
                    rows.push(...renderVariantSubItems(variant, variantKey, isSelectionMode, cancelledItemIds));
                  }
                  
                  return rows;
                }).filter(Boolean).flat(); // Remove null entries and flatten
              } else {
                // For regular items, check for cancellations
                const isCancelled = isItemCancelled(item);
                const cancelledItemIds = getCancelledItemNumbers(item);
                const totalQuantity = getTotalQuantity(item);
                const cancelledQuantity = cancelledItemIds.length;
                const remainingQuantity = totalQuantity - cancelledQuantity;

                const rows = [];

                // Show remaining quantity row
                if (remainingQuantity > 0) {
                  rows.push(
                    <TableRow key={item.itemKey}>
                      {isSelectionMode && (
                        <TableCell>
                          <Checkbox
                            checked={selectedItems.has(item.itemKey)}
                            onChange={() => handleItemSelect(item.itemKey)}
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ textTransform: "lowercase" }}>
                        {item.menuItemId?.code ||
                          `i${String(index + 1).padStart(3, "0")}`}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "lowercase" }}
                          >
                            {item.menuItemId?.name}
                          </Typography>
                          <Typography variant="caption" color="error">
                            *
                          </Typography>
                          {shouldExpand && (
                            <IconButton
                              size="small"
                              onClick={() => toggleSidesExpansion(item.itemKey)}
                              sx={{ p: 0.5 }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textTransform: "lowercase" }}>
                        standard
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {hasItemNotes(item) && (
                            <Tooltip
                              title={getAllNotes(item)}
                              arrow
                              placement="top"
                              PopperProps={{
                                style: { zIndex: 10001 },
                              }}
                            >
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                <NotesIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasItemCustomizations(item) && (
                            <IconButton size="small" sx={{ p: 0.5 }}>
                              <CustomizeIcon size="16" color="#6200EE" />
                            </IconButton>
                          )}
                          {!hasItemNotes(item) &&
                            !hasItemCustomizations(item) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            src={getImageDisplayUrl(item.userOrder.userProfilePic)}
                            sx={{ width: 24, height: 24 }}
                          >
                            {item.userOrder.userFullName?.charAt(0) || "U"}
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{ textTransform: "lowercase" }}
                          >
                            {item.userOrder.userFullName || "user"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isSelectionMode ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenCancellationDialog(
                                  item,
                                  item.userIndex,
                                  item.itemIndex,
                                  "decrease"
                                )
                              }
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: "#FEF2F2",
                                color: "#DC2626",
                                "&:hover": {
                                  backgroundColor: "#DC2626",
                                  color: "white",
                                },
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography
                              variant="body2"
                              sx={{ minWidth: 20, textAlign: "center" }}
                            >
                              {remainingQuantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenCancellationDialog(
                                  item,
                                  item.userIndex,
                                  item.itemIndex,
                                  "increase"
                                )
                              }
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: "#F0F9FF",
                                color: "#0369A1",
                                "&:hover": {
                                  backgroundColor: "#0369A1",
                                  color: "white",
                                },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {remainingQuantity}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "lowercase" }}
                        >
                          {getPriceDisplay(item)} / per unit
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                // Show cancelled item - consolidated row when all quantities are cancelled
                if (cancelledQuantity > 0 && cancelledItemIds.length > 0) {
                  // Check if ALL quantities of this item are cancelled
                  const isFullyCancelled = cancelledQuantity === totalQuantity;

                  if (isFullyCancelled) {
                    // Show ONE consolidated row for fully cancelled item
                    const cancelledLabel = cancelledItemIds.some(
                      (itemId) =>
                        getCancellationStatusForItem(item, itemId, null) ===
                        "pending_approval"
                    )
                      ? "pending cancellation"
                      : "cancelled";
                    rows.push(
                      <TableRow key={`${item.itemKey}-cancelled-full`} sx={{ opacity: 0.6 }}>
                        {isSelectionMode && <TableCell></TableCell>}
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          {item.menuItemId?.code ||
                            `i${String(index + 1).padStart(3, "0")}`}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: "lowercase",
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              {item.menuItemId?.name} (all quantities {cancelledLabel})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textTransform: "lowercase" }}>
                          <Typography
                            sx={{
                              textDecoration: "line-through",
                              color: "text.secondary",
                            }}
                          >
                            standard
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              src={getImageDisplayUrl(item.userOrder.userProfilePic)}
                              sx={{ width: 24, height: 24, opacity: 0.6 }}
                            >
                              {item.userOrder.userFullName?.charAt(0) || "U"}
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{
                                textTransform: "lowercase",
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              {item.userOrder.userFullName || "user"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: "line-through",
                              color: "text.secondary",
                            }}
                          >
                            {cancelledQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              textTransform: "lowercase",
                              textDecoration: "line-through",
                              color: "text.secondary",
                            }}
                          >
                            {getPriceDisplay(item)} / per unit
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  } else {
                    // Show individual cancelled items for partial cancellation
                    cancelledItemIds.forEach((itemId) => {
                      const itemLabel =
                        getCancellationStatusForItem(item, itemId, null) ===
                        "pending_approval"
                          ? "pending cancellation"
                          : "cancelled";
                      rows.push(
                        <TableRow key={`${item.itemKey}-cancelled-${itemId}`} sx={{ opacity: 0.6 }}>
                          {isSelectionMode && <TableCell></TableCell>}
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            {item.menuItemId?.code ||
                              `i${String(index + 1).padStart(3, "0")}`}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  textTransform: "lowercase",
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                }}
                              >
                                {item.menuItemId?.name} ({itemLabel})
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textTransform: "lowercase" }}>
                            <Typography
                              sx={{
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              standard
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                src={getImageDisplayUrl(item.userOrder.userProfilePic)}
                                sx={{ width: 24, height: 24, opacity: 0.6 }}
                              >
                                {item.userOrder.userFullName?.charAt(0) || "U"}
                              </Avatar>
                              <Typography
                                variant="caption"
                                sx={{
                                  textTransform: "lowercase",
                                  textDecoration: "line-through",
                                  color: "text.secondary",
                                }}
                              >
                                {item.userOrder.userFullName || "user"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              1
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: "lowercase",
                                textDecoration: "line-through",
                                color: "text.secondary",
                              }}
                            >
                              {getPriceDisplay(item)} / per unit
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  }
                }

                // Show customizations and sides if expanded
                if (shouldExpand && isExpanded && remainingQuantity > 0) {
                  rows.push(
                    ...renderSubItems(
                      item,
                      item.itemKey,
                      isSelectionMode,
                      cancelledItemIds
                    )
                  );
                }

                return (
                  <React.Fragment key={item.itemKey}>{rows}</React.Fragment>
                );
              }
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render billing details
  const renderBillingDetails = () => {
    return (
      <Box>
        {renderBillingItems()}

        {/* Payment Summary - Show group totals (full order information) - amounts right-aligned like mobile */}
        <PaymentSummary>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
              total subtotal:
            </Typography>
            <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
              {formatPrice(subtotalBeforeTax, getOrderCurrency())}
            </Typography>
          </Box>

          {/* Tax Breakdown - Use group tax breakdown */}
          {taxBreakdown.map((tax, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                {tax.type}:
              </Typography>
              <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                {formatPrice(tax.amount, getOrderCurrency())}
              </Typography>
            </Box>
          ))}

          {/* Display total (including tax, before tip) - Use group total */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, textTransform: "lowercase" }}
            >
              total total:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, textTransform: "lowercase" }}
            >
              {formatPrice(subtotal, getOrderCurrency())}
            </Typography>
          </Box>
        </PaymentSummary>

        {/* Tip Section - Only show if tip field is enabled and tip percentages are available */}
        {(() => {
          // Check if tip field is enabled
          const enableTipField = orderData?.locationId?.paymentSettings?.enableTipField || 
                                 orderData?.paymentSettings?.enableTipField || 
                                 false;
          
          // Get tip percentages array
          const tipPercentage = orderData?.locationId?.paymentSettings?.tipPercentage || 
                                orderData?.paymentSettings?.tipPercentage || 
                                [];
          
          // Only show tip section if enableTipField is true and tipPercentage has values
          const shouldShowTipSection = enableTipField && 
                                       Array.isArray(tipPercentage) && 
                                       tipPercentage.length > 0 && 
                                       tipPercentage.some(tip => tip > 0);
          
          if (!shouldShowTipSection) {
            return null;
          }
          
          return (
        <TipSection>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, textTransform: "lowercase" }}
          >
            add tip
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <TipButton
              variant={selectedTip === "no tip" ? "contained" : "outlined"}
              onClick={() => handleTipSelect("no tip")}
              sx={{ textTransform: "lowercase" }}
            >
              no tip
            </TipButton>
                {/* Render tip percentage buttons from API response */}
                {tipPercentage.filter(tip => tip > 0).map((tip) => (
              <TipButton
                key={tip}
                variant={selectedTip === tip ? "contained" : "outlined"}
                onClick={() => handleTipSelect(tip)}
                sx={{ textTransform: "lowercase" }}
              >
                {tip}%
              </TipButton>
            ))}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ minWidth: "120px", textTransform: "lowercase" }}
            >
              gratuity/service tip
            </Typography>
            <TextField
              size="small"
              value={customTip}
              onChange={handleCustomTipChange}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1 }}>
                    {getCurrencySymbol(getOrderCurrency())}
                  </Typography>
                ),
              }}
              sx={{ width: 100 }}
            />
          </Box>
        </TipSection>
          );
        })()}
      </Box>
    );
  };

  // Handle dialog close with cleanup
  const handleClose = () => {
    setPendingCancellations([]);
    setCancelledItems({});
    setSelectedItems(new Set());
    setIsSelectionMode(false);
    onClose();
  };

  if (!open) return null;

  return (
    <>
    <CustomOverlay>
      <CustomDialog
        ref={dialogRef}
        style={{ left: dialogPosition.x, top: dialogPosition.y }}
      >
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.25rem" },
                textTransform: "lowercase",
                ml: { xs: 1, sm: 2 },
              }}
            >
              order details
            </Typography>
            {/* Pending Cancellations Badge */}
            {pendingCancellations.length > 0 && (
              <Chip
                label={`${pendingCancellations.length} pending`}
                size="small"
                sx={{
                  backgroundColor: "#DC2626",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "lowercase",
                }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              sx={{
                color: "#7C3AED",
                backgroundColor: "#f0f4ff",
                "&:hover": {
                  backgroundColor: "#7C3AED",
                  color: "white",
                },
              }}
            >
              <ChatIcon fontSize="small" />
            </IconButton>
            {/* <IconButton
              size="small"
              sx={{
                color: "#7C3AED",
                backgroundColor: "#f0f4ff",
                "&:hover": {
                  backgroundColor: "#7C3AED",
                  color: "white",
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton> */}
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "#7C3AED",
                backgroundColor: "#f0f4ff",
                "&:hover": {
                  backgroundColor: "#7C3AED",
                  color: "white",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogHeader>

        <ContentContainer>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress size={60} sx={{ color: "#7C3AED" }} />
            </Box>
          ) : !orderData ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#64748B",
              }}
            >
              <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
                no order data available
              </Typography>
            </Box>
          ) : (
            <ScrollableContainer>
              {/* Business Info Header */}
              <BusinessHeader>
                <BusinessInfo>
                  <BusinessLogo
                    src={getImageDisplayUrl(orderData.locationId?.locationProfilePhoto, MEDIA_URL)}
                  >
                    {orderData.businessId?.businessName?.charAt(0) || "B"}
                  </BusinessLogo>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        textTransform: "lowercase",
                        fontSize: { xs: "1rem", md: "1.2rem" },
                      }}
                    >
                      {orderData.businessId?.businessName || "business name"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "lowercase" }}
                    >
                      {orderData.locationId?.businessLocationName || "location"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textTransform: "lowercase" }}
                    >
                      tax invoice
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textTransform: "lowercase" }}
                    >
                      {orderData.locationId?.emailAddress ||
                        "business@email.com"}
                    </Typography>
                  </Box>
                </BusinessInfo>

                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "1rem" },
                      display: "block",
                      textTransform: "lowercase",
                    }}
                  >
                    vat#:{" "}
                    {orderData.locationId?.businessVatNumber || "aiu89790piaf"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "1rem" },
                      display: "block",
                      textTransform: "lowercase",
                    }}
                  >
                    reg#:{" "}
                    {orderData.locationId?.businessRegistrationNumber ||
                      "2017/iso2009-23"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "1rem" },
                      display: "block",
                      textTransform: "lowercase",
                    }}
                  >
                    tel:{" "}
                    {orderData.locationId?.phoneNumber?.number ||
                      "020-123195923"}
                  </Typography>
                </Box>
              </BusinessHeader>

              {/* User Card */}
              <UserCard>
                <UserInfo>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src={getImageDisplayUrl(orderData.userId?.image?.profilePic)}
                      sx={{
                        width: { xs: 40, md: 60 },
                        height: { xs: 40, md: 60 },
                      }}
                    >
                      {orderData.userId?.fullName?.charAt(0) || "U"}
                    </Avatar>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 0 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, textTransform: "lowercase" }}
                      >
                        order by {orderData.userId?.fullName || "user name"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: "lowercase" }}
                      >
                        {orderData.orderMode === "seat_down"
                          ? "seat down"
                          : "collect"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ textTransform: "lowercase" }}
                      >
                        today at {moment(orderData.createdAt).format("h:mm a")}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <AccessTimeIcon sx={{ fontSize: "0.9rem" }} />
                        <Typography variant="caption">
                          {getTimeAgo(orderData.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, textTransform: "lowercase" }}
                    >
                      order id: #{orderData.orderNumber || "248936500"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textTransform: "lowercase" }}
                    >
                      table number: 34
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textTransform: "lowercase" }}
                    >
                      pax: {orderData.groupTotalQuantity || 12}
                    </Typography>
                    {orderData.groupOrder?.userOrders &&
                      orderData.groupOrder.userOrders.length > 1 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textTransform: "lowercase",
                            color: "#7C3AED",
                            fontWeight: 600,
                          }}
                        >
                          group order ({orderData.groupOrder.userOrders.length}{" "}
                          users)
                        </Typography>
                      )}
                  </Box>
                </UserInfo>
              </UserCard>

              {/* Search Field with Delete Icon */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="search items..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{ p: 0.5 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        "& fieldset": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover fieldset": {
                          borderColor: "#7C3AED",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#7C3AED",
                        },
                      },
                      "& .MuiInputBase-input": {
                        textTransform: "lowercase",
                        "&::placeholder": {
                          textTransform: "lowercase",
                          opacity: 0.7,
                        },
                      },
                    }}
                  />
                  <Tooltip
                    title={
                      isSelectionMode
                        ? "exit selection mode"
                        : "enable selection mode"
                    }
                    arrow
                  >
                    <IconButton
                      onClick={handleDeleteClick}
                      sx={{
                        color: isSelectionMode ? "white" : "#6200EE",
                        backgroundColor: isSelectionMode
                          ? "#7C3AED"
                          : "#F2E9FF",
                        border: `2px solid ${
                          isSelectionMode ? "#7C3AED" : "#e5e7eb"
                        }`,
                        "&:hover": {
                          backgroundColor: isSelectionMode
                            ? "#6D28D9"
                            : "#f3f4f6",
                          color: isSelectionMode ? "white" : "#374151",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Tabs */}
              <CustomTabs value={activeTab} onChange={handleTabChange}>
                <Tab label="order details" />
                <Tab label="billing details" />
              </CustomTabs>

              {/* Tab Content */}
              {activeTab === 0 ? (
                <Box>
                  {renderOrderItems()}

                  {/* Cancellation Actions - Show if there are pending cancellations */}
                  {pendingCancellations.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: 1,
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleClearAllCancellations}
                        sx={{
                          color: "#fff",
                          borderColor: "#d1d5db",
                          textTransform: "lowercase",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "#f3f4f6",
                            borderColor: "#9ca3af",
                          },
                        }}
                      >
                        clear all
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleSubmitAllCancellations}
                        disabled={cancellationMutation.isLoading}
                        sx={{
                          backgroundColor: "#6200EE",
                          color: "white",
                          textTransform: "lowercase",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "#7C3AED",
                          },
                        }}
                      >
                        {cancellationMutation.isLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CircularProgress
                              size={16}
                              sx={{ color: "white" }}
                            />
                            submitting...
                          </Box>
                        ) : (
                          `submit all cancellations (${pendingCancellations.length})`
                        )}
                      </Button>
                    </Box>
                  )}

                  {/* Order Status Section */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: 1,
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "lowercase" }}
                      >
                        order status:
                      </Typography>
                      <Chip
                        label={orderData.orderStatus || "closed"}
                        size="small"
                        sx={{ textTransform: "lowercase", color: "#6200EE" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "lowercase" }}
                      >
                        payment status:
                      </Typography>
                      <Chip
                        label={orderData.paymentStatus || "pending"}
                        size="small"
                        color={
                          orderData.paymentStatus === "paid"
                            ? "success"
                            : "warning"
                        }
                        sx={{ textTransform: "lowercase" }}
                      />
                    </Box>

                    {/* Payment Status Indicators */}
                    {upfrontPayment > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                          p: 1,
                          backgroundColor: "#fff3cd",
                          borderRadius: 1,
                          border: "1px solid #ffeaa7",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "lowercase",
                            fontWeight: 600,
                            color: "#856404",
                          }}
                        >
                          upfront payment required:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "lowercase",
                            fontWeight: 600,
                            color: "#856404",
                          }}
                        >
                          {formatPrice(upfrontPayment, getOrderCurrency())} (
                          {orderData.paymentSettings.upfrontPercentage}%)
                        </Typography>
                      </Box>
                    )}

                    {showPaymentCompletedBanner && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                          p: 1,
                          backgroundColor: "#d4edda",
                          borderRadius: 1,
                          border: "1px solid #c3e6cb",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "lowercase",
                            fontWeight: 600,
                            color: "#155724",
                          }}
                        >
                          payment status:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "lowercase",
                            fontWeight: 600,
                            color: "#155724",
                          }}
                        >
                          ✓ fully paid
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box>
                  {renderBillingDetails()}

                  {/* Cancellation Actions - Show if there are pending cancellations */}
                  {pendingCancellations.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: 1,
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleClearAllCancellations}
                        sx={{
                          color: "#fff",
                          borderColor: "#d1d5db",
                          textTransform: "lowercase",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "#f3f4f6",
                            borderColor: "#9ca3af",
                          },
                        }}
                      >
                        clear all
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleSubmitAllCancellations}
                        disabled={cancellationMutation.isLoading}
                        sx={{
                          backgroundColor: "#6200EE",
                          color: "white",
                          textTransform: "lowercase",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "#7C3AED",
                          },
                        }}
                      >
                        {cancellationMutation.isLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CircularProgress
                              size={16}
                              sx={{ color: "white" }}
                            />
                            submitting...
                          </Box>
                        ) : (
                          `submit all cancellations (${pendingCancellations.length})`
                        )}
                      </Button>
                    </Box>
                  )}

                  {/* Grand Total and Action Buttons - Only in Billing Tab - amounts right-aligned like mobile */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: "#f0f4ff",
                      borderRadius: 1,
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "lowercase" }}
                        >
                          your subtotal:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "lowercase" }}
                        >
                          {formatPrice(userSubtotalBeforeTax, getOrderCurrency())}
                        </Typography>
                      </Box>
                      {userTaxBreakdown.map((tax, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "lowercase" }}
                          >
                            {tax.type}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "lowercase" }}
                          >
                            {formatPrice(tax.amount, getOrderCurrency())}
                          </Typography>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "lowercase", fontWeight: 600 }}
                        >
                          your total:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "lowercase", fontWeight: 600 }}
                        >
                          {formatPrice(userAmountToPay, getOrderCurrency())}
                        </Typography>
                      </Box>
                      {tipAmount > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "lowercase" }}
                          >
                            tip:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "lowercase" }}
                          >
                            {formatPrice(tipAmount, getOrderCurrency())}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Only show "already paid / need to pay" when the user has an
                        actual payment obligation (grandTotal > 0). This prevents
                        owners/team-members who are NOT part of a split from seeing a
                        misleading "already paid: ₹X" box derived from proportional
                        group-payment accounting. */}
                    {grandTotal > 0 &&
                    orderData?.groupOrder?.userOrders?.find(
                      (userOrder) =>
                        userOrder.userId?._id === currentUserId ||
                        userOrder.userId?._id === storedUserId
                    )?.amountPaid > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "#e8f5e8",
                          borderRadius: 1,
                          border: "1px solid #4caf50",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              textTransform: "lowercase",
                              color: "#2e7d32",
                              fontWeight: 600,
                            }}
                          >
                            already paid:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textTransform: "lowercase",
                              color: "#2e7d32",
                              fontWeight: 700,
                            }}
                          >
                            {formatPrice(
                              orderData.groupOrder.userOrders
                                .find(
                                  (userOrder) =>
                                    userOrder.userId?._id === currentUserId ||
                                    userOrder.userId?._id === storedUserId
                                )?.amountPaid || 0,
                              getOrderCurrency()
                            )}
                          </Typography>
                        </Box>
                        {(() => {
                          const perUserRefundTotal =
                            orderData?.refundSummary?.perUser?.reduce(
                              (sum, userRefund) =>
                                sum + (userRefund?.totalRefunded || 0),
                              0
                            ) || 0;
                          const refundTotal =
                            perUserRefundTotal ||
                            orderData?.refundSummary?.totalRefundAmount ||
                            0;

                          if (!refundTotal) return null;

                          return (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  textTransform: "lowercase",
                                  color: "#FF4C51",
                                  fontWeight: 600,
                                }}
                              >
                                refunded:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textTransform: "lowercase",
                                  color: "#FF4C51",
                                  fontWeight: 700,
                                }}
                              >
                                {formatPrice(refundTotal, getOrderCurrency())}
                              </Typography>
                            </Box>
                          );
                        })()}
                        {(upfrontPayment > 0 || remainingPayment > 0) && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: "lowercase",
                                color: "#2e7d32",
                                fontWeight: 600,
                              }}
                            >
                              need to pay:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: "lowercase",
                                color: "#2e7d32",
                                fontWeight: 700,
                              }}
                            >
                              {formatPrice(remainingPayment, getOrderCurrency())}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Upfront Payment Section */}
                    {upfrontPayment > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "#fff3cd",
                          borderRadius: 1,
                          border: "1px solid #ffeaa7",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "lowercase",
                            color: "#856404",
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          upfront payment required (
                          {orderData.paymentSettings.upfrontPercentage}% of
                          total):
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            textTransform: "lowercase",
                            color: "#856404",
                            fontWeight: 700,
                          }}
                        >
                          {formatPrice(upfrontPayment, getOrderCurrency())}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ textTransform: "lowercase", color: "#856404" }}
                        >
                          pay this amount first to process your order
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          textTransform: "lowercase",
                        }}
                      >
                        {isPayForAll ? "total grand total" : "your grand total"}:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          textTransform: "lowercase",
                        }}
                      >
                        {formatPrice(grandTotal, getOrderCurrency())}
                      </Typography>
                    </Box>

                    {/* Payment Complete Indicator */}
                    {showPaymentCompletedBanner && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "#d4edda",
                          borderRadius: 1,
                          border: "1px solid #c3e6cb",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            textTransform: "lowercase",
                            fontWeight: 700,
                            color: "#155724",
                          }}
                        >
                          ✓ payment completed
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "lowercase",
                            color: "#155724",
                            mt: 0.5,
                          }}
                        >
                          your order is fully paid and ready
                        </Typography>
                      </Box>
                    )}

                    <ActionButtons>
                     
                      {/* Show pay button based on upfront payment and order status */}
                      {showPaymentButton && (
                        <ActionButton
                          className="pay"
                          onClick={() => {
                            // Calculate payment amount including tip
                            let paymentAmount = 0;
                            if (upfrontPayment > 0) {
                              // For upfront payment, add tip to the upfront amount
                              paymentAmount = upfrontPayment + tipAmount;
                            } else if (remainingPayment > 0) {
                              // For remaining payment, add tip to the remaining amount
                              paymentAmount = remainingPayment + tipAmount;
                            } else {
                              // For full payment, use grand total which already includes tip
                              paymentAmount = grandTotal;
                            }
                            handlePayment(paymentAmount.toFixed(2));
                          }}
                          disabled={paymentLoading}
                          sx={{
                            textTransform: "lowercase",
                            opacity: paymentLoading ? 0.7 : 1,
                            cursor: paymentLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {paymentLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress
                                size={16}
                                sx={{ color: "white" }}
                              />
                              processing...
                            </Box>
                          ) : (() => {
                            const orderStatus = orderData?.orderStatus;
                            const upfrontPercentage = orderData?.paymentSettings?.upfrontPercentage || 0;
                            const currency = getOrderCurrency();
                            
                            // Calculate total payment amount including tip
                            let totalPaymentAmount = 0;
                            let baseAmount = 0;
                            
                            // If upfront is done (upfrontPayment === 0) or upfront percentage is 0, and order is closed,
                            // always show remaining amount instead of upfront
                            const isUpfrontDone = upfrontPayment === 0 || upfrontPercentage === 0;
                            if (isUpfrontDone && orderStatus === "closed order") {
                              if (remainingPayment > 0) {
                                baseAmount = remainingPayment;
                                totalPaymentAmount = remainingPayment + tipAmount;
                                if (tipAmount > 0) {
                                  return `pay remaining ${formatPrice(totalPaymentAmount, currency)}`;
                                }
                                return `pay remaining ${formatPrice(remainingPayment, currency)}`;
                              }
                              return "pay";
                            }
                            
                            // Otherwise, show upfront if available, then remaining, then just "pay"
                            if (upfrontPayment > 0) {
                              baseAmount = upfrontPayment;
                              totalPaymentAmount = upfrontPayment + tipAmount;
                              if (tipAmount > 0) {
                                return `pay ${formatPrice(totalPaymentAmount, currency)} upfront`;
                              }
                              return `pay ${formatPrice(upfrontPayment, currency)} upfront`;
                            } else if (remainingPayment > 0) {
                              baseAmount = remainingPayment;
                              totalPaymentAmount = remainingPayment + tipAmount;
                              if (tipAmount > 0) {
                                return `pay remaining ${formatPrice(totalPaymentAmount, currency)}`;
                              }
                              return `pay remaining ${formatPrice(remainingPayment, currency)}`;
                            } else {
                              if (tipAmount > 0) {
                                return `pay ${formatPrice(grandTotal, currency)}`;
                              }
                              return "pay";
                            }
                          })()}
                        </ActionButton>
                      )}
                    </ActionButtons>
                  </Box>
                </Box>
              )}
            </ScrollableContainer>
          )}
        </ContentContainer>

        <ResizeHandle onMouseDown={handleResizeStart} />
      </CustomDialog>

      {/* Cancellation Dialog */}
      <CancellationDialog
        open={cancellationDialogOpen}
        onClose={handleCancellationDialogClose}
        orderData={orderData}
        selectedCartItem={selectedCartItem}
        onSuccess={handleCancellationSuccess}
        onSubmit={handleCancellationSubmit}
        isLoading={cancellationMutation.isLoading}
      />

    </CustomOverlay>

      {/* Payment Error Dialog — rendered outside CustomOverlay to float above it */}
      <Dialog
        open={paymentErrorDialog.open}
        onClose={() => setPaymentErrorDialog({ open: false, message: "" })}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 10002 }}
        PaperProps={{ sx: { borderRadius: 3, textAlign: "center", px: 1 } }}
      >
        <DialogTitle sx={{ pt: 3, pb: 1, fontWeight: 700, fontSize: 18 }}>
          payment error
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {paymentErrorDialog.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            please wait for a team member to process your request
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, pt: 1 }}>
          <Button
            onClick={() => setPaymentErrorDialog({ open: false, message: "" })}
            sx={{ color: "#fff", textTransform: "none" }}
          >
            cancel
          </Button>
          <Button
            onClick={handleManualPaymentRequest}
            disabled={manualPaymentLoading}
            sx={{ color: "#fff", fontWeight: 700, textTransform: "none" }}
          >
            {manualPaymentLoading ? <CircularProgress size={16} /> : "manual payment request"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderDetailsDialog;
